/**
 * React-query hooks for properties + rooms — V2 admin module.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { isMissingColumn } from "./useContractFlowReady";
import { fetchAllByColumn } from "./usePropertyRentPaidStats";
import { useAuth } from "@/contexts/AuthContext";
import { useOperator } from "@/hooks/useOperator";
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { resolveCoverImage } from "@/lib/v2-images";
import { parseDateOnly } from "@/lib/roomAvailability";
import type {
  Apartment,
  ApartmentBankOverride,
  ApartmentListItem,
  CollectionMode,
  EffectiveBank,
  EffectiveRep,
  EffectiveRevenue,
  PlatformBankAccount,
  Property,
  PropertyBankOverride,
  PropertyListItem,
  RevenueModel,
  Room,
  RoomBankOverride,
} from "@/types/property";
import type { Lessor } from "@/types/lessor";
import { operatorStamped } from "@/lib/operator-stamped";

const QK = {
  list: () => ["v2", "properties", "list"] as const,
  one: (id: number) => ["v2", "properties", id] as const,
  rooms: (propertyId: number) =>
    ["v2", "rooms", "by-property", propertyId] as const,
  room: (id: number) => ["v2", "rooms", id] as const,
  apartments: (propertyId: number) =>
    ["v2", "apartments", "by-property", propertyId] as const,
  apartment: (id: number) => ["v2", "apartments", id] as const,
  apartmentRooms: (apartmentId: number) =>
    ["v2", "rooms", "by-apartment", apartmentId] as const,
  propertyBankOverride: (id: number) =>
    ["v2", "property-bank-override", id] as const,
  roomBankOverride: (id: number) => ["v2", "room-bank-override", id] as const,
  apartmentBankOverride: (id: number) =>
    ["v2", "apartment-bank-override", id] as const,
  platformBankAccounts: () => ["v2", "platform-bank-accounts"] as const,
  platformLessor: () => ["v2", "platform-lessor"] as const,
  bookings: (roomId: number) => ["v2", "bookings", "by-room", roomId] as const,
  booking: (id: number) => ["v2", "bookings", id] as const,
  auditLogs: (resourceType: string, resourceId: string) =>
    ["v2", "audit-logs", resourceType, resourceId] as const,
};

// ─── Properties list ──────────────────────────────────────────────────────

/**
 * `owner_visible_properties` on TWO INDEPENDENT AXES (two-axis port,
 * 20261021190000): "has CoLiviq approved this property?" and "has the owner
 * onboarded?" used to be collapsed into one combined state string, which is
 * what let a card-ordering change become a permissions change — one
 * predicate could not say both "CoLiviq refused this" and "the owner still
 * has to sign".
 *
 *  approval:
 *   • pending  — still in admin review.
 *   • approved
 *   • rejected
 *
 *  onboarding:
 *   • outstanding — the owner has work to do: no property_owners link, or a
 *                   required management contract isn't signed.
 *   • in_review   — contract documents submitted, CoLiviq checking them.
 *   • done
 */
export type OwnerApproval = "pending" | "approved" | "rejected";
export type OwnerOnboarding = "outstanding" | "in_review" | "done";

export type OwnerVisibleProperty = {
  property_id: number;
  approval: OwnerApproval;
  onboarding: OwnerOnboarding;
};

export type OwnerPropertyAxes = {
  approval: OwnerApproval;
  onboarding: OwnerOnboarding;
};

/** True when the owner still has to sign — the "Sign contract now" gate. */
export function ownerMustSign(axes: OwnerPropertyAxes | undefined): boolean {
  if (!axes) return false;
  return axes.approval !== "rejected" && axes.onboarding === "outstanding";
}

/** True when the property is fully live: approved AND onboarded. */
export function ownerPropertyIsLive(
  axes: OwnerPropertyAxes | undefined
): boolean {
  if (!axes) return true;
  return axes.approval === "approved" && axes.onboarding === "done";
}

/** True when the owner has signed and CoLiviq is checking the documents. */
export function ownerOnboardingInReview(
  axes: OwnerPropertyAxes | undefined
): boolean {
  return axes?.onboarding === "in_review";
}

/**
 * True when the error says the RPC itself isn't there — i.e. this build is
 * running against a database that hasn't had 20260815090000 applied yet.
 * Both owner-scoping paths below degrade to their previous behaviour on this
 * rather than throwing: an owner whose portal is briefly missing a card is a
 * far smaller failure than one whose portal is empty.
 */
function isMissingFunction(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const e = error as { code?: string; message?: string };
  if (e.code === "42883" || e.code === "PGRST202") return true;
  return /function .* does not exist|could not find the function/i.test(
    String(e.message ?? "")
  );
}

/**
 * When `ownerOnlyUserId` is set, only properties the owner account holds are
 * returned — resolved server-side by `owner_visible_properties`, which covers
 * all four ways an account attaches to a building (property_owners link, their
 * own submission, properties.lessor_id, or an apartment they own). It used to
 * be a client-side `property_owners` filter, which missed every property held
 * through an apartment; `apartments` is admin-only under RLS, so that widening
 * cannot happen in the browser.
 *
 * The id is only a cache key here — the RPC scopes to auth.uid() itself.
 */
/**
 * How many ids go into one `.in()`. Kept well clear of the URL length a
 * PostgREST GET can carry — the filter is serialised into the query string,
 * and a few hundred bigints is already a long one.
 */
export const PROPERTY_ID_CHUNK = 200;

/** PostgREST's per-response row cap on Supabase. Requesting one more row than
 *  a page holds is how you find out whether there IS another page. */
export const ROW_PAGE_SIZE = 1000;

/**
 * Plan the (chunk, page) requests needed to read every child row for a set of
 * properties.
 *
 * Split out from the fetch so the arithmetic is testable without a database —
 * the bug this exists to prevent is silent, and a wrong page boundary would
 * reintroduce it in exactly the same undetectable way.
 */
export function chunkPropertyIds<T>(
  ids: T[],
  size: number = PROPERTY_ID_CHUNK
): T[][] {
  if (ids.length === 0) return [];
  const out: T[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

/**
 * Every child row of these properties, scoped and paged.
 *
 * An unfiltered `select()` was returning at most PostgREST's max-rows and
 * saying nothing about the truncation, so counts derived from it were silently
 * wrong for whichever rows fell past the cap. Two rules keep that from coming
 * back: only ask for the properties on screen, and keep asking until a page
 * comes back short.
 *
 * Ordered by id so the pages tile without gaps or repeats — an unordered
 * `range()` is not a stable window over the table.
 */
/**
 * The client without the generated types, for the paging helpers: their table
 * and column names are runtime arguments the types can't follow, so callers
 * type the rows through `Row` instead.
 */
export const untypedSupabase = supabase as unknown as SupabaseClient;

async function fetchAllByPropertyId<Row>(
  table: string,
  select: string,
  propertyIds: Array<number | string>
): Promise<Row[]> {
  if (propertyIds.length === 0) return [];
  const out: Row[] = [];
  for (const chunk of chunkPropertyIds(propertyIds)) {
    let from = 0;
    // Bounded by the table, not by trust: each pass must return a full page to
    // earn another request.
    for (;;) {
      const { data, error } = await untypedSupabase
        .from(table)
        .select(select)
        .in("property_id", chunk)
        .order("id", { ascending: true })
        .range(from, from + ROW_PAGE_SIZE - 1);
      if (error) throw error;
      const rows = (data ?? []) as Row[];
      out.push(...rows);
      if (rows.length < ROW_PAGE_SIZE) break;
      from += ROW_PAGE_SIZE;
    }
  }
  return out;
}

/**
 * An apartment counts as hidden (COL-463) when every one of its rooms is
 * hidden, INCLUDING an apartment with zero rooms — a deliberate product
 * decision (vacuous truth: nothing published yet is nothing to show), not an
 * oversight.
 */
export function isApartmentHidden(
  rooms: Array<{ is_hidden: boolean | null }>
): boolean {
  return rooms.every((r) => r.is_hidden);
}

/**
 * A property counts as "all apartments hidden" only when it has at least one
 * apartment and every one of them is hidden. A property with no apartments at
 * all (e.g. a plain WG building) is never true here — there's nothing to hide.
 */
export function isAllApartmentsHidden(
  apartmentRoomSets: Array<Array<{ is_hidden: boolean | null }>>
): boolean {
  return (
    apartmentRoomSets.length > 0 && apartmentRoomSets.every(isApartmentHidden)
  );
}

export function useProperties(
  ownerOnlyUserId?: string,
  enabled: boolean = true
) {
  // properties/rooms are PUBLIC-read (listing parity), so RLS won't scope them
  // to an operator's own inventory — the admin queries must. Staff (no operator
  // portal) keep the global view; an operator sees only their org's rows.
  const { isOperatorPortal } = useAuth();
  const { operator } = useOperator();
  const operatorId = isOperatorPortal ? (operator?.id ?? null) : null;
  return useQuery({
    // Don't run an operator's query before we know their org id, or it would
    // fetch every operator's inventory for a beat.
    enabled: enabled && (!isOperatorPortal || operatorId != null),
    queryKey: ownerOnlyUserId
      ? [...QK.list(), "owner", ownerOnlyUserId]
      : operatorId
        ? [...QK.list(), "operator", operatorId]
        : QK.list(),
    queryFn: async (): Promise<PropertyListItem[]> => {
      let propertyQuery = supabase.from("properties").select("*");
      if (operatorId)
        propertyQuery = propertyQuery.eq("operator_id", operatorId);
      const { data: properties, error: pErr } = await propertyQuery.order(
        "title",
        { ascending: true }
      );
      if (pErr) throw pErr;

      let propertyRows = (properties ?? []) as Property[];
      if (ownerOnlyUserId) {
        const { data: visible, error: vErr } = await supabase.rpc(
          "owner_visible_properties"
        );
        if (vErr && !isMissingFunction(vErr)) throw vErr;

        let allowed: Set<number>;
        if (vErr) {
          // Pre-migration database: the narrower property_owners resolution
          // this replaced. Misses apartment-held properties, as it always did.
          const { data: poRows, error: poErr } = await supabase
            .from("property_owners")
            .select("property_id")
            .eq("user_id", ownerOnlyUserId);
          if (poErr) throw poErr;
          allowed = new Set(
            (poRows ?? []).map((r: { property_id: number }) =>
              Number(r.property_id)
            )
          );
        } else {
          allowed = new Set(
            (visible ?? []).map((r: OwnerVisibleProperty) =>
              Number(r.property_id)
            )
          );
        }
        propertyRows = propertyRows.filter((p) => allowed.has(Number(p.id)));
      }

      // Scoped to the properties actually being listed, and PAGED.
      //
      // Both of these were unbounded `select()` calls over the whole table.
      // PostgREST caps a response at its max-rows setting (1000 on Supabase)
      // and says nothing about having truncated — so once the platform held
      // more than 1000 rooms, whichever rooms fell past the cap simply had no
      // row here and their property's card read "0 Rooms". Neither query
      // ordered its results either, so the rows that fell off were effectively
      // arbitrary — in practice the newest ids, which is why a property
      // created five minutes ago was the one showing zero.
      //
      // Operator scoping (COL-353) rides on propertyIds: the property query
      // above is already filtered to the operator's org, so every child row
      // fetched here belongs to it too.
      const propertyIds = propertyRows.map((p) => p.id);
      const rooms = await fetchAllByPropertyId<{
        id: number | string;
        property_id: number | string;
        apartment_id: number | string | null;
        status: string | null;
        available: boolean | null;
        is_hidden: boolean | null;
      }>(
        "rooms",
        "id, property_id, apartment_id, status, available, is_hidden",
        propertyIds
      );

      // Apartment counts per property (NEW layer). Admin-only RLS means this
      // returns no rows for non-admins; the count then falls back to 0, which
      // is the correct display for the owner portal where apartments aren't
      // surfaced.
      const apartments = await fetchAllByPropertyId<{
        id: number | string;
        property_id: number | string;
        lessor_id: string | null;
        owner_user_id: string | null;
      }>(
        "apartments",
        "id, property_id, lessor_id, owner_user_id",
        propertyIds
      );
      const aptCountByProp = new Map<string, number>();
      // Owners of the apartments inside each property. A property whose own
      // lessor_id is NULL is not ownerless — its apartments usually carry the
      // owners — so the card resolves its owner label from here.
      const aptLessorsByProp = new Map<string, Set<string>>();
      /* The owner ACCOUNTS behind those apartments, which is a different thing
         from the lessor: an owner-submitted property gets owner_user_id set and
         no lessor at all (a lessor is a billing entity, created later by
         staff), and the card called that "No owner" while the submitter was
         sitting right there on every apartment. */
      const aptOwnersByProp = new Map<string, Set<string>>();
      // Apartment ids grouped by property, to answer "are ALL of this
      // property's apartments hidden" (COL-463) once room visibility is
      // known below.
      const apartmentIdsByProp = new Map<string, Array<number | string>>();
      for (const a of apartments) {
        const key = String(a.property_id);
        aptCountByProp.set(key, (aptCountByProp.get(key) ?? 0) + 1);
        const ids = apartmentIdsByProp.get(key) ?? [];
        ids.push(a.id);
        apartmentIdsByProp.set(key, ids);
        if (a.lessor_id) {
          const set = aptLessorsByProp.get(key) ?? new Set<string>();
          set.add(a.lessor_id);
          aptLessorsByProp.set(key, set);
        }
        if (a.owner_user_id) {
          const set = aptOwnersByProp.get(key) ?? new Set<string>();
          set.add(a.owner_user_id);
          aptOwnersByProp.set(key, set);
        }
      }

      /* Names for those accounts. Degrades to no name rather than a thrown
         query — a card without an owner label is still a usable card. */
      const ownerNameById = new Map<string, string>();
      const allOwnerIds = Array.from(
        new Set(Array.from(aptOwnersByProp.values()).flatMap((s) => [...s]))
      );
      if (allOwnerIds.length > 0) {
        const { data: owners } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", allOwnerIds);
        for (const o of (owners ?? []) as Array<{
          id: string;
          name: string | null;
          email: string | null;
        }>) {
          const label = o.name?.trim() || o.email?.trim();
          if (label) ownerNameById.set(o.id, label);
        }
      }

      // Key the map by stringified property_id to defend against PostgREST's
      // BIGINT serialization quirks. Both properties.id and rooms.property_id
      // are bigint columns; the Supabase client returns them as JS numbers
      // when the value fits in a safe integer, but the narrowing isn't
      // always consistent across queries — observed case in dev: property
      // ids came back as numbers while rooms.property_id came back as
      // strings, causing a Map<number, …> lookup to silently miss every
      // match and leave rooms_count = 0 on every card. Stringifying both
      // sides removes the variance.
      // Occupancy comes from bookings (see occupiedRoomIdsFromBookings), not the
      // stale rooms.status/available flags. The owner portal cannot read other
      // tenants' bookings, so it keeps the flag-based count.
      const occupiedRoomIds = ownerOnlyUserId
        ? null
        : await fetchOccupiedRoomIds(rooms.map((r) => r.id));
      const roomsByProp = new Map<
        string,
        Array<{
          id: number | string;
          status: string | null;
          available: boolean | null;
          is_hidden: boolean | null;
        }>
      >();
      // Same string-keying rationale as roomsByProp, one level down: an
      // apartment counts as hidden (COL-463) when every one of ITS rooms is
      // hidden, so this is keyed by apartment_id rather than property_id.
      const roomsByApartment = new Map<
        string,
        Array<{ is_hidden: boolean | null }>
      >();
      for (const r of rooms) {
        const propKey = String(r.property_id);
        const arr = roomsByProp.get(propKey) ?? [];
        arr.push({
          id: r.id,
          status: r.status,
          available: r.available,
          is_hidden: r.is_hidden,
        });
        roomsByProp.set(propKey, arr);

        if (r.apartment_id == null) continue;
        const aptKey = String(r.apartment_id);
        const aptRooms = roomsByApartment.get(aptKey) ?? [];
        aptRooms.push({ is_hidden: r.is_hidden });
        roomsByApartment.set(aptKey, aptRooms);
      }

      return propertyRows.map((p): PropertyListItem => {
        const propRooms = roomsByProp.get(String(p.id)) ?? [];
        const visibleRooms = propRooms.filter((r) => !r.is_hidden);
        const occupied = visibleRooms.filter((r) =>
          occupiedRoomIds
            ? occupiedRoomIds.has(Number(r.id))
            : r.status === "occupied" || r.available === false
        ).length;
        const propApartmentIds = apartmentIdsByProp.get(String(p.id)) ?? [];
        return {
          ...p,
          rooms_count: visibleRooms.length,
          occupied_count: occupied,
          apartments_count: aptCountByProp.get(String(p.id)) ?? 0,
          apartment_lessor_ids: Array.from(
            aptLessorsByProp.get(String(p.id)) ?? []
          ),
          apartment_owner_names: Array.from(
            aptOwnersByProp.get(String(p.id)) ?? []
          )
            .map((id) => ownerNameById.get(id))
            .filter((n): n is string => !!n),
          all_apartments_hidden: isAllApartmentsHidden(
            propApartmentIds.map((id) => roomsByApartment.get(String(id)) ?? [])
          ),
          cover_image: resolveCoverImage(p.images, p.image_url),
        };
      });
    },
  });
}

/**
 * Onboarding axes for every property the calling owner holds, keyed by id.
 *
 * Same RPC `useProperties(ownerId)` scopes with — the grid needs the axes as
 * well as the id set, and react-query dedupes the two calls only when they
 * share a key, which they don't. One extra round trip per page load, against
 * a much larger correctness win: without it, the grid cannot tell a live
 * property from one that was never onboarded.
 */
export function useOwnerVisiblePropertyStates(enabled: boolean = true) {
  return useQuery({
    enabled,
    queryKey: ["v2", "owner-visible-properties"],
    queryFn: async (): Promise<Map<number, OwnerPropertyAxes>> => {
      const { data, error } = await supabase.rpc("owner_visible_properties");
      // No axes pre-migration → every card defaults to live, which is the
      // grid's behaviour before this change.
      if (error) {
        if (isMissingFunction(error)) return new Map();
        throw error;
      }
      const rows = (data ?? []) as OwnerVisibleProperty[];
      return new Map(
        rows.map((r) => [
          Number(r.property_id),
          { approval: r.approval, onboarding: r.onboarding },
        ])
      );
    },
  });
}

/** Per-property contract-signature gate from owner_properties_contract_gate. */
export type OwnerPropertyGate = {
  property_id: number;
  contract_required: boolean;
  enabled: boolean;
};

/**
 * For every property the caller owns: whether a CoLiviq management contract
 * is required and whether it's signed. Cards for properties with
 * enabled=false stay disabled until the owner completes onboarding.
 */
export function useOwnerPropertiesContractGate(enabled: boolean = true) {
  return useQuery({
    enabled,
    queryKey: ["v2", "owner-properties-contract-gate"],
    queryFn: async (): Promise<Map<number, OwnerPropertyGate>> => {
      const { data, error } = await supabase.rpc(
        "owner_properties_contract_gate"
      );
      if (error) throw error;
      const rows = (data ?? []) as OwnerPropertyGate[];
      return new Map(rows.map((r) => [Number(r.property_id), r]));
    },
  });
}

// ─── Single property + rooms ──────────────────────────────────────────────

export function useProperty(id: number | null | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ["v2", "properties", "noop"],
    queryFn: async (): Promise<Property | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("properties")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as Property | null;
    },
    enabled: !!id,
  });
}

export function usePropertyRooms(propertyId: number | null | undefined) {
  return useQuery({
    queryKey: propertyId ? QK.rooms(propertyId) : ["v2", "rooms", "noop"],
    queryFn: async (): Promise<Room[]> => {
      if (!propertyId) return [];
      // Admin-controlled sort_order first (drag-reordered by editors), with
      // alphabetical title as a stable tie-breaker for rooms that haven't
      // been manually reordered yet. nullsFirst:false pushes unsorted rooms
      // to the end so editors see the curated order at the top of the list.
      const { data, error } = await supabase
        .from("rooms")
        .select("*")
        .eq("property_id", propertyId)
        .order("sort_order", { ascending: true, nullsFirst: false })
        .order("title", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Room[];
    },
    enabled: !!propertyId,
  });
}

/**
 * One-off, uncached fetch of a property's rooms (id, apartment_id, title
 * only). Used by ApartmentEditSheet's "number of rooms" fill to read a fresh
 * count/title snapshot immediately before computing the gap to create,
 * rather than relying on usePropertyRooms' possibly long-stale cached data —
 * narrows (does not eliminate — only a DB-side atomic count+insert would)
 * the window in which a concurrent room creation elsewhere goes unseen.
 */
export async function fetchPropertyRoomsSnapshot(
  propertyId: number
): Promise<Array<{ apartment_id: number | null; title: string }>> {
  const { data, error } = await supabase
    .from("rooms")
    .select("apartment_id, title")
    .eq("property_id", propertyId);
  if (error) throw error;
  return (data ?? []) as Array<{ apartment_id: number | null; title: string }>;
}

export function useRoom(id: number | null | undefined) {
  return useQuery({
    queryKey: id ? QK.room(id) : ["v2", "rooms", "noop"],
    queryFn: async (): Promise<Room | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("rooms")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as Room | null;
    },
    enabled: !!id,
  });
}

// ─── Apartments (owner-owned units) ───────────────────────────────────────

/**
 * Apartments for a property, each with derived room/occupancy/revenue stats.
 * Read-only surface for the V2 admin Apartment layer (COL-225).
 */
export function useApartments(propertyId: number | null | undefined) {
  return useQuery({
    queryKey: propertyId
      ? QK.apartments(propertyId)
      : ["v2", "apartments", "noop"],
    queryFn: async (): Promise<ApartmentListItem[]> => {
      if (!propertyId) return [];
      const { data: apts, error: aErr } = await supabase
        .from("apartments")
        .select("*")
        .eq("property_id", propertyId)
        .order("sort_order", { ascending: true })
        .order("label", { ascending: true });
      if (aErr) throw aErr;

      const apartments = (apts ?? []) as Apartment[];
      if (apartments.length === 0) return [];

      // One query for all rooms of the property, then group by apartment_id.
      const { data: rooms, error: rErr } = await supabase
        .from("rooms")
        .select("id, apartment_id, price, status, available, is_hidden")
        .eq("property_id", propertyId);
      if (rErr) throw rErr;
      const occupiedRoomIds = await fetchPropertyOccupiedRoomIds(propertyId);

      const byApt = new Map<
        string,
        Array<{
          id: number;
          price: number | null;
          status: string | null;
          available: boolean | null;
          is_hidden: boolean | null;
        }>
      >();
      for (const r of (rooms ?? []) as Array<{
        id: number;
        apartment_id: number | string | null;
        price: number | null;
        status: string | null;
        available: boolean | null;
        is_hidden: boolean | null;
      }>) {
        if (r.apartment_id == null) continue;
        const key = String(r.apartment_id);
        const arr = byApt.get(key) ?? [];
        arr.push({
          id: r.id,
          price: r.price,
          status: r.status,
          available: r.available,
          is_hidden: r.is_hidden,
        });
        byApt.set(key, arr);
      }

      return apartments.map((a): ApartmentListItem => {
        // Every room the apartment has, live or not — occupancy is tracked
        // against the whole unit, not just what is published on the website.
        const allAptRooms = byApt.get(String(a.id)) ?? [];
        const visibleAptRooms = allAptRooms.filter((r) => !r.is_hidden);
        const occupied = allAptRooms.filter((r) =>
          occupiedRoomIds.has(Number(r.id))
        ).length;
        const revenue = visibleAptRooms.reduce(
          (sum, r) => sum + (Number(r.price) || 0),
          0
        );
        return {
          ...a,
          rooms_count: allAptRooms.length,
          occupied_count: occupied,
          rooms_revenue: revenue,
          room_ids: visibleAptRooms.map((r) => r.id),
        };
      });
    },
    enabled: !!propertyId,
  });
}

export function useApartment(id: number | null | undefined) {
  return useQuery({
    queryKey: id ? QK.apartment(id) : ["v2", "apartments", "noop"],
    queryFn: async (): Promise<Apartment | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("apartments")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as Apartment | null;
    },
    enabled: !!id,
  });
}

export function useApartmentRooms(apartmentId: number | null | undefined) {
  return useQuery({
    queryKey: apartmentId
      ? QK.apartmentRooms(apartmentId)
      : ["v2", "rooms", "noop"],
    queryFn: async (): Promise<Room[]> => {
      if (!apartmentId) return [];
      const { data, error } = await supabase
        .from("rooms")
        .select("*")
        .eq("apartment_id", apartmentId)
        .order("sort_order", { ascending: true })
        .order("title", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Room[];
    },
    enabled: !!apartmentId,
  });
}

// ─── Apartment mutations ────────────────────────────────────────────────────

/**
 * Create a new apartment under a property. The caller supplies the full
 * insert payload (label is required at the DB level). Invalidates the
 * property's apartments list + the properties list (apartments_count).
 */
export function useCreateApartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<Apartment> & { property_id: number; label: string }
    ): Promise<Apartment> => {
      const { data, error } = await supabase
        .from("apartments")
        .insert(operatorStamped<"apartments">(input))
        .select()
        .single();
      if (error) throw error;
      return data as Apartment;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: QK.apartments(vars.property_id) });
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
    },
  });
}

/** Update an existing apartment. propertyId is passed for cache invalidation. */
export function useUpdateApartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      propertyId: number;
      patch: Partial<Apartment>;
    }): Promise<Apartment> => {
      const { data, error } = await supabase
        .from("apartments")
        .update(params.patch)
        .eq("id", params.id)
        .select()
        .single();
      if (error) throw error;
      return data as Apartment;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: QK.apartments(vars.propertyId) });
      qc.invalidateQueries({ queryKey: QK.apartment(vars.id) });
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
    },
  });
}

/**
 * Assign/unassign existing rooms to an apartment in one save (COL-225, opt-in
 * model — there is no standalone create-room flow, so "add rooms" means
 * linking existing property rooms via rooms.apartment_id).
 *
 * `assign` sets rooms.apartment_id → apartmentId; `unassign` clears it to NULL
 * (room falls back to property-level resolution). Both are bulk updates keyed
 * by id. Invalidates the apartment's room list, the property's room list, the
 * apartments list (derived room/occupancy/revenue stats), and the properties
 * list.
 */
export function useAssignRoomsToApartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      apartmentId: number;
      propertyId: number;
      assign: number[];
      unassign: number[];
    }): Promise<void> => {
      if (params.assign.length > 0) {
        const { error } = await supabase
          .from("rooms")
          .update({ apartment_id: params.apartmentId })
          .in("id", params.assign);
        if (error) throw error;
      }
      if (params.unassign.length > 0) {
        const { error } = await supabase
          .from("rooms")
          .update({ apartment_id: null })
          .in("id", params.unassign);
        if (error) throw error;
      }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: QK.apartmentRooms(vars.apartmentId) });
      qc.invalidateQueries({ queryKey: QK.rooms(vars.propertyId) });
      qc.invalidateQueries({ queryKey: QK.apartments(vars.propertyId) });
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
      for (const id of [...vars.assign, ...vars.unassign]) {
        qc.invalidateQueries({ queryKey: QK.room(id) });
      }
    },
  });
}

/**
 * Create a new room, optionally pre-assigned to an apartment via
 * apartment_id. Invalidates the property's room list, the apartment's room
 * list (if apartment_id was set), and the properties list (room counts).
 */
export function useCreateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<Room> & {
        property_id: number;
        title: string;
        price: number;
      }
    ): Promise<Room> => {
      const { data, error } = await supabase
        .from("rooms")
        .insert(operatorStamped<"rooms">(input))
        .select()
        .single();
      if (error) throw error;
      return data as Room;
    },
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: QK.rooms(vars.property_id) });
      if (data.apartment_id) {
        qc.invalidateQueries({
          queryKey: QK.apartmentRooms(data.apartment_id),
        });
      }
      qc.invalidateQueries({ queryKey: QK.apartments(vars.property_id) });
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
    },
  });
}

// ─── Bank overrides ───────────────────────────────────────────────────────

export function usePropertyBankOverride(propertyId: number | null | undefined) {
  return useQuery({
    queryKey: propertyId
      ? QK.propertyBankOverride(propertyId)
      : ["v2", "property-bank-override", "noop"],
    queryFn: async (): Promise<PropertyBankOverride | null> => {
      if (!propertyId) return null;
      const { data, error } = await supabase
        .from("property_bank_overrides")
        .select("*")
        .eq("property_id", propertyId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as PropertyBankOverride | null;
    },
    enabled: !!propertyId,
  });
}

export function useRoomBankOverride(roomId: number | null | undefined) {
  return useQuery({
    queryKey: roomId
      ? QK.roomBankOverride(roomId)
      : ["v2", "room-bank-override", "noop"],
    queryFn: async (): Promise<RoomBankOverride | null> => {
      if (!roomId) return null;
      const { data, error } = await supabase
        .from("room_bank_overrides")
        .select("*")
        .eq("room_id", roomId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as RoomBankOverride | null;
    },
    enabled: !!roomId,
  });
}

export function useApartmentBankOverride(
  apartmentId: number | null | undefined
) {
  return useQuery({
    queryKey: apartmentId
      ? QK.apartmentBankOverride(apartmentId)
      : ["v2", "apartment-bank-override", "noop"],
    queryFn: async (): Promise<ApartmentBankOverride | null> => {
      if (!apartmentId) return null;
      const { data, error } = await supabase
        .from("apartment_bank_overrides")
        .select("*")
        .eq("apartment_id", apartmentId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as ApartmentBankOverride | null;
    },
    enabled: !!apartmentId,
  });
}

// ─── Platform (CoLiviq) bank accounts ──────────────────────────────────────
//
// Both hooks expose CoLiviq's OWN bank data, which operator-portal users
// (SaaS customers, COL-352) must never see. Gating `enabled` here covers
// every consumer (PropertyEditSheet, ApartmentEditSheet, ApartmentDetail,
// useManagementInvoiceActions) in one place; return shapes are unchanged —
// consumers already default the disabled undefined to []/null.

/** All CoLiviq-held bank accounts, default-first then by label. */
export function usePlatformBankAccounts() {
  const { isOperatorPortal } = useAuth();
  return useQuery({
    queryKey: QK.platformBankAccounts(),
    enabled: !isOperatorPortal,
    queryFn: async (): Promise<PlatformBankAccount[]> => {
      const { data, error } = await supabase
        .from("platform_bank_accounts")
        .select("*")
        .order("is_default", { ascending: false })
        .order("label", { ascending: true });
      if (error) throw error;
      return (data ?? []) as PlatformBankAccount[];
    },
  });
}

/** The lessor flagged as CoLiviq itself (is_platform). Null if none set. */
export function usePlatformLessor() {
  const { isOperatorPortal } = useAuth();
  return useQuery({
    queryKey: QK.platformLessor(),
    enabled: !isOperatorPortal,
    queryFn: async (): Promise<Lessor | null> => {
      const { data, error } = await supabase
        .from("lessors")
        .select("*")
        .eq("is_platform", true)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as Lessor | null;
    },
  });
}

/**
 * Present the CoLiviq lessor's Standard-Bankkonto as a PlatformBankAccount so
 * the rest of the UI can treat it like any platform account. Synthetic id of
 * -1 marks it as "not a platform_bank_accounts row" — it's display-only and is
 * never written back as an override. Null if the lessor has no IBAN.
 */
export function platformAccountFromLessor(
  lessor: Lessor | null | undefined
): PlatformBankAccount | null {
  if (!lessor?.bank_iban) return null;
  return {
    id: -1,
    label: lessor.name,
    holder: lessor.bank_holder ?? lessor.name,
    iban: lessor.bank_iban,
    bic: lessor.bank_bic,
    bank_name: lessor.bank_name,
    is_default: true,
    created_at: lessor.created_at,
    updated_at: lessor.updated_at,
  };
}

/**
 * Resolve the CoLiviq account for an apartment: explicit apartment override →
 * property override → the designated CoLiviq lessor's bank account. The
 * lessor's Standard-Bankkonto is the source of truth for the default account,
 * so admins maintain it in one place. Falls back to a legacy
 * platform_bank_accounts default row only if no platform lessor is configured.
 * Returns null if nothing resolves.
 */
export function resolvePlatformAccount(args: {
  apartment?: Apartment | null;
  property?: Property | null;
  accounts: PlatformBankAccount[];
  coliviqLessor?: Lessor | null;
}): PlatformBankAccount | null {
  const { apartment, property, accounts, coliviqLessor } = args;
  const id =
    apartment?.platform_bank_account_id ??
    property?.platform_bank_account_id ??
    null;
  if (id != null) {
    const found = accounts.find((a) => a.id === id);
    if (found) return found;
  }
  return (
    platformAccountFromLessor(coliviqLessor) ??
    accounts.find((a) => a.is_default) ??
    null
  );
}

// ─── Inheritance resolver ─────────────────────────────────────────────────

/**
 * Resolve the effective representor across the inheritance chain.
 * Priority: room override → apartment override → apartment owner (lessor)
 * → property override → property lessor default.
 *
 * `source: "apartment"` means the value was set ON this apartment
 * (apartments.rep_override). An apartment that merely carries its own owner
 * reports `"lessor"` — the value is that owner's default, not an override —
 * so the inheritance badge can tell the two apart.
 *
 * ON `apartmentLessor`: it must be the lessor that belongs to THIS apartment —
 * either its direct `lessor_id` or the one behind its `owner_user_id` account.
 * Never pass the PROPERTY's lessor here. Callers resolve the owner through
 * `effectiveApartmentOwner`, whose last step falls back to the property's
 * lessor; hand that result in unfiltered and this branch claims the chain for
 * an apartment that owns nothing, silently skipping `property.rep_override`.
 *
 * Blank names fall through at every level, matching the edge function's `str()`
 * gates: an owner with no `rep_name` must reach the property's override rather
 * than terminate the chain on an empty value.
 *
 * Must stay in sync with the same chain in the create-contract edge function.
 */
export function resolveEffectiveRep(args: {
  room?: Room | null;
  apartment?: Apartment | null;
  apartmentLessor?: Lessor | null;
  property: Property | null;
  lessor: Lessor | null;
}): EffectiveRep {
  const { room, apartment, apartmentLessor, property, lessor } = args;
  if (room?.lessor_override?.rep_name?.trim()) {
    return {
      name: room.lessor_override.rep_name ?? null,
      role: room.lessor_override.rep_role ?? null,
      note: room.lessor_override.rep_note ?? null,
      source: "room",
    };
  }
  if (apartment?.rep_override?.name?.trim()) {
    return {
      name: apartment.rep_override.name ?? null,
      role: apartment.rep_override.role ?? null,
      note: apartment.rep_override.note ?? null,
      source: "apartment",
    };
  }
  if (apartmentLessor?.rep_name?.trim()) {
    return {
      name: apartmentLessor.rep_name,
      role: apartmentLessor.rep_role,
      note: apartmentLessor.rep_note,
      source: "lessor",
    };
  }
  if (property?.rep_override?.name?.trim()) {
    return {
      name: property.rep_override.name ?? null,
      role: property.rep_override.role ?? null,
      note: property.rep_override.note ?? null,
      source: "property",
    };
  }
  if (lessor) {
    return {
      name: lessor.rep_name,
      role: lessor.rep_role,
      note: lessor.rep_note,
      source: "lessor",
    };
  }
  return { name: null, role: null, note: null, source: "none" };
}

/**
 * Resolve the effective bank account across the inheritance chain.
 * Priority: room_bank_override → apartment_bank_override → apartment owner
 * → property_bank_override → property lessor default.
 *
 * `source` distinguishes an override from a default, exactly as in
 * `resolveEffectiveRep`: only `apartment_bank_override` — a row someone created
 * FOR this apartment — reports `"apartment"`. An apartment falling back to its
 * owner's bank details reports `"lessor"`. InheritanceBadge renders
 * `source === level` as the amber "Override active", so labelling the owner's
 * default `"apartment"` told admins this apartment had its own rent account;
 * they then had no way to know that editing the OWNER's bank details would move
 * this apartment's rent too.
 */
export function resolveEffectiveBank(args: {
  roomBank?: RoomBankOverride | null;
  apartmentBank?: ApartmentBankOverride | null;
  apartmentLessor?: Lessor | null;
  propertyBank?: PropertyBankOverride | null;
  lessor: Lessor | null;
}): EffectiveBank {
  const { roomBank, apartmentBank, apartmentLessor, propertyBank, lessor } =
    args;
  if (roomBank?.iban) {
    return {
      holder: roomBank.holder,
      iban: roomBank.iban,
      bic: roomBank.bic,
      bank_name: roomBank.bank_name,
      source: "room",
    };
  }
  if (apartmentBank?.iban) {
    return {
      holder: apartmentBank.holder,
      iban: apartmentBank.iban,
      bic: apartmentBank.bic,
      bank_name: apartmentBank.bank_name,
      source: "apartment",
    };
  }
  if (apartmentLessor?.bank_iban) {
    return {
      holder: apartmentLessor.bank_holder,
      iban: apartmentLessor.bank_iban,
      bic: apartmentLessor.bank_bic,
      bank_name: apartmentLessor.bank_name,
      source: "lessor",
    };
  }
  if (propertyBank?.iban) {
    return {
      holder: propertyBank.holder,
      iban: propertyBank.iban,
      bic: propertyBank.bic,
      bank_name: propertyBank.bank_name,
      source: "property",
    };
  }
  if (lessor?.bank_iban) {
    return {
      holder: lessor.bank_holder,
      iban: lessor.bank_iban,
      bic: lessor.bank_bic,
      bank_name: lessor.bank_name,
      source: "lessor",
    };
  }
  return {
    holder: null,
    iban: null,
    bic: null,
    bank_name: null,
    source: "none",
  };
}

/**
 * Resolve an apartment's effective revenue/collection config by inheriting any
 * unset field from its property (COL-225). Each apartment column is nullable;
 * NULL means "inherit". The property always has a concrete collection_mode +
 * revenue_model, so the resolved values are never null for those.
 */
export function resolveEffectiveRevenue(args: {
  apartment?: Apartment | null;
  property: Property | null;
}): EffectiveRevenue {
  const { apartment, property } = args;

  const collection_mode: CollectionMode =
    apartment?.collection_mode ??
    property?.collection_mode ??
    "company_collects";
  const revenue_model: RevenueModel =
    apartment?.revenue_model ?? property?.revenue_model ?? "full_rent";

  // Fee fields travel together with whichever level supplies the revenue model.
  const feeFromApartment = apartment?.revenue_model != null;
  const coliviq_fee_pct = feeFromApartment
    ? (apartment?.coliviq_fee_pct ?? null)
    : (property?.coliviq_fee_pct ?? null);
  const coliviq_fee_amount = feeFromApartment
    ? (apartment?.coliviq_fee_amount ?? null)
    : (property?.coliviq_fee_amount ?? null);

  return {
    collection_mode,
    revenue_model,
    coliviq_fee_pct,
    coliviq_fee_amount,
    source: {
      collection_mode:
        apartment?.collection_mode != null ? "apartment" : "property",
      revenue_model:
        apartment?.revenue_model != null ? "apartment" : "property",
      fee: feeFromApartment ? "apartment" : "property",
    },
  };
}

/**
 * Resolve whether automated rent reminders/dunning run for a unit, inheriting
 * the property default when the apartment doesn't override (COL-225 pattern).
 * apartment.rent_reminders_enabled (nullable) wins; else the property default;
 * else true (reminders on) when neither is known.
 */
export function resolveRemindersEnabled(args: {
  apartment?: { rent_reminders_enabled?: boolean | null } | null;
  property?: { rent_reminders_enabled?: boolean | null } | null;
}): boolean {
  const { apartment, property } = args;
  return (
    apartment?.rent_reminders_enabled ??
    property?.rent_reminders_enabled ??
    true
  );
}

export { resolveEffectiveKeyPickup } from "@/lib/key-pickup";

/**
 * Resolve the representor name an apartment reports through the full
 * inheritance chain (room override is out of scope here — this is a
 * property/apartment-level roll-up, same granularity as the Owners count).
 * `lessorById` only needs to contain the apartment's OWN lessor (if any);
 * passing an unrelated lessor here would let its rep_name leak in as if it
 * were an apartment override, per resolveEffectiveRep's contract.
 *
 * `lessorIdByOwnerUser` resolves the apartment's OWN lessor when it holds no
 * `lessor_id` of its own but is linked to an owner ACCOUNT
 * (`apartment.owner_user_id`) whose `lessor_id` backfill was skipped (the
 * account maps to zero or several lessor candidates — see
 * `apartments_fill_lessor_from_owner`). Reading `lessor_id` alone here
 * would under-count exactly like it did before ApartmentDetail.tsx and the
 * Owners count were fixed to also resolve through the owner account.
 */
export function apartmentRepresentorName(
  apartment: Apartment,
  property: Property | null,
  lessorById: Map<string, Lessor> | undefined,
  propertyLessor: Lessor | null,
  lessorIdByOwnerUser?: Record<string, string>
): string | null {
  const ownLessorId =
    apartment.lessor_id ??
    (apartment.owner_user_id
      ? (lessorIdByOwnerUser?.[apartment.owner_user_id] ?? null)
      : null);
  const apartmentLessor =
    ownLessorId != null ? (lessorById?.get(ownLessorId) ?? null) : null;
  const eff = resolveEffectiveRep({
    apartment,
    apartmentLessor,
    property,
    lessor: propertyLessor,
  });
  return eff.name?.trim() || null;
}

export type ContractRepresentorContext = {
  lessorById?: Map<string, Lessor>;
  propertyLessor?: Lessor | null;
  propertyOwnerLessor?: Lessor | null;
  lessorIdByOwnerUser?: Record<string, string>;
};

/**
 * The name an apartment's contracts would print after "vertreten durch", or
 * null for none: apartmentRepresentorName resolves nothing AND the legacy
 * flat `properties.representor_name` column — create-contract's last resort,
 * which resolveEffectiveRep doesn't read — is blank too.
 *
 * Apartment-level on purpose, like ApartmentEditSheet's save check (COL-466):
 * a room-level override covers that one room's contracts, not the next room
 * added to the apartment.
 *
 * `propertyOwnerLessor` is create-contract's fallback for a property with no
 * `lessor_id` of its own (its property_owners lessor). The edge function only
 * consults it for an apartment with no owner lessor of its own, so the same
 * gate applies here — an apartment whose own owner has a blank rep_name does
 * NOT pick up the property owner's. For the same reason `lessorIdByOwnerUser`
 * should be scoped to this property's property_owners rows, which is all
 * create-contract reads for an apartment named only by owner account.
 */
export function apartmentContractRepresentorName(
  apartment: Apartment,
  property: Property | null,
  ctx: ContractRepresentorContext
): string | null {
  const ownLessorId =
    apartment.lessor_id ??
    (apartment.owner_user_id
      ? (ctx.lessorIdByOwnerUser?.[apartment.owner_user_id] ?? null)
      : null);
  const fallbackLessor = property?.lessor_id
    ? (ctx.propertyLessor ?? null)
    : ownLessorId
      ? null
      : (ctx.propertyOwnerLessor ?? null);
  return (
    apartmentRepresentorName(
      apartment,
      property,
      ctx.lessorById,
      fallbackLessor,
      ctx.lessorIdByOwnerUser
    ) ||
    property?.representor_name?.trim() ||
    null
  );
}

/** The apartments whose contracts would print no representor at all. */
export function apartmentsMissingRepresentor<A extends Apartment>(
  apartments: A[],
  property: Property | null,
  ctx: ContractRepresentorContext
): A[] {
  return apartments.filter(
    (a) => !apartmentContractRepresentorName(a, property, ctx)
  );
}

/**
 * Summarise whether a property's apartments share the same revenue/collection
 * config and owner/CoLiviq accounts, or diverge. Used to decide whether the
 * property level can show a single representative value or must show a
 * "Varies by apartment (N distinct)" state (APARTMENT_REVENUE_BANK_SPEC.md §4.3).
 *
 * Computed from the apartment rows alone (cheap — no per-apartment bank fetch):
 * owner divergence is inferred from differing lessor_id / platform account.
 *
 * `representorContext` is optional so existing callers that don't need the
 * representor dimension aren't forced to fetch lessors; omitting it just
 * treats every apartment as reporting no representor (all "∅", uniform).
 */
export function summarizeApartmentConfig(
  apartments: Apartment[],
  property: Property | null,
  representorContext?: {
    lessorById?: Map<string, Lessor>;
    propertyLessor?: Lessor | null;
    lessorIdByOwnerUser?: Record<string, string>;
  }
): {
  count: number;
  collectionMode: { uniform: boolean; distinct: number };
  revenueModel: { uniform: boolean; distinct: number };
  owner: { uniform: boolean; distinct: number };
  platformAccount: { uniform: boolean; distinct: number };
  representor: { uniform: boolean; distinct: number };
} {
  const dim = (values: Array<string | number | null>) => {
    const distinct = new Set(values.map((v) => (v == null ? "∅" : String(v))));
    return { uniform: distinct.size <= 1, distinct: distinct.size };
  };
  const resolved = apartments.map((a) =>
    resolveEffectiveRevenue({ apartment: a, property })
  );
  return {
    count: apartments.length,
    collectionMode: dim(resolved.map((r) => r.collection_mode)),
    revenueModel: dim(resolved.map((r) => r.revenue_model)),
    owner: dim(apartments.map((a) => a.lessor_id)),
    platformAccount: dim(
      apartments.map(
        (a) =>
          a.platform_bank_account_id ??
          property?.platform_bank_account_id ??
          null
      )
    ),
    representor: dim(
      apartments.map((a) =>
        apartmentRepresentorName(
          a,
          property,
          representorContext?.lessorById,
          representorContext?.propertyLessor ?? null,
          representorContext?.lessorIdByOwnerUser
        )
      )
    ),
  };
}

// ─── Mutations ────────────────────────────────────────────────────────────

export function useUpdateProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      patch: Partial<Property>;
    }): Promise<Property> => {
      const { data, error } = await supabase
        .from("properties")
        .update(params.patch)
        .eq("id", params.id)
        .select()
        .single();
      if (error) throw error;
      return data as Property;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
      qc.invalidateQueries({ queryKey: QK.one(vars.id) });
    },
  });
}

/**
 * Delete a property. Cascades to rooms via the FK constraint
 * (rooms.property_id ON DELETE CASCADE).
 */
export function useDeleteProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      const { error } = await supabase.from("properties").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
      qc.invalidateQueries({ queryKey: QK.one(id) });
    },
  });
}

export function useDeleteRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      propertyId: number;
    }): Promise<void> => {
      const { error } = await supabase
        .from("rooms")
        .delete()
        .eq("id", params.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: QK.rooms(vars.propertyId) });
      qc.invalidateQueries({ queryKey: QK.room(vars.id) });
      qc.invalidateQueries({ queryKey: ["v2", "properties"] });
    },
  });
}

export function useUpdateRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      propertyId: number;
      patch: Partial<Room>;
    }): Promise<Room> => {
      const { data, error } = await supabase
        .from("rooms")
        .update(params.patch)
        .eq("id", params.id)
        .select()
        .single();
      if (error) throw error;
      return data as Room;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: QK.rooms(vars.propertyId) });
      qc.invalidateQueries({ queryKey: QK.room(vars.id) });
    },
  });
}

/**
 * Persist a new drag-reordered sequence for the rooms of a property.
 *
 * The caller passes the rooms in their new visual order; we re-number them
 * 10, 20, 30, … so a future "insert between" can squeeze into the gaps
 * without re-numbering everything. PostgREST doesn't have a true batched
 * UPDATE-with-different-values, so we fan out one PATCH per row inside a
 * Promise.all. Acceptable here because reorder events are user-initiated
 * (rare) and rooms-per-property is bounded (~25 today, ~50 worst case).
 *
 * Optimistic update keeps the new order visible during the round-trip;
 * rollback on error restores the pre-drag state from the snapshot.
 */
export function useReorderRooms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      propertyId: number;
      orderedRoomIds: number[];
    }): Promise<void> => {
      const updates = params.orderedRoomIds.map((id, i) =>
        supabase
          .from("rooms")
          .update({ sort_order: (i + 1) * 10 })
          .eq("id", id)
      );
      const results = await Promise.all(updates);
      const firstError = results.find((r) => r.error)?.error;
      if (firstError) throw firstError;
    },
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: QK.rooms(vars.propertyId) });
      const prev = qc.getQueryData<Room[]>(QK.rooms(vars.propertyId));
      if (prev) {
        const byId = new Map(prev.map((r) => [r.id, r]));
        const next = vars.orderedRoomIds
          .map((id, i) => {
            const r = byId.get(id);
            return r ? { ...r, sort_order: (i + 1) * 10 } : null;
          })
          .filter((r): r is Room => r !== null);
        qc.setQueryData(QK.rooms(vars.propertyId), next);
      }
      return { prev };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(QK.rooms(vars.propertyId), ctx.prev);
      }
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: QK.rooms(vars.propertyId) });
    },
  });
}

export function useUpsertPropertyBankOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      property_id: number;
      holder: string | null;
      iban: string | null;
      bic: string | null;
      bank_name: string | null;
    }): Promise<PropertyBankOverride> => {
      const { data, error } = await supabase
        .from("property_bank_overrides")
        .upsert(input, { onConflict: "property_id" })
        .select()
        .single();
      if (error) throw error;
      return data as PropertyBankOverride;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: QK.propertyBankOverride(vars.property_id),
      });
    },
  });
}

export function useDeletePropertyBankOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (propertyId: number): Promise<void> => {
      const { error } = await supabase
        .from("property_bank_overrides")
        .delete()
        .eq("property_id", propertyId);
      if (error) throw error;
    },
    onSuccess: (_data, propertyId) => {
      qc.invalidateQueries({ queryKey: QK.propertyBankOverride(propertyId) });
    },
  });
}

export function useUpsertRoomBankOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      room_id: number;
      holder: string | null;
      iban: string | null;
      bic: string | null;
      bank_name: string | null;
    }): Promise<RoomBankOverride> => {
      const { data, error } = await supabase
        .from("room_bank_overrides")
        .upsert(input, { onConflict: "room_id" })
        .select()
        .single();
      if (error) throw error;
      return data as RoomBankOverride;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: QK.roomBankOverride(vars.room_id) });
    },
  });
}

export function useDeleteRoomBankOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (roomId: number): Promise<void> => {
      const { error } = await supabase
        .from("room_bank_overrides")
        .delete()
        .eq("room_id", roomId);
      if (error) throw error;
    },
    onSuccess: (_data, roomId) => {
      qc.invalidateQueries({ queryKey: QK.roomBankOverride(roomId) });
    },
  });
}

export function useUpsertApartmentBankOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      apartment_id: number;
      holder: string | null;
      iban: string | null;
      bic: string | null;
      bank_name: string | null;
    }): Promise<ApartmentBankOverride> => {
      const { data, error } = await supabase
        .from("apartment_bank_overrides")
        .upsert(input, { onConflict: "apartment_id" })
        .select()
        .single();
      if (error) throw error;
      return data as ApartmentBankOverride;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: QK.apartmentBankOverride(vars.apartment_id),
      });
    },
  });
}

export function useDeleteApartmentBankOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (apartmentId: number): Promise<void> => {
      const { error } = await supabase
        .from("apartment_bank_overrides")
        .delete()
        .eq("apartment_id", apartmentId);
      if (error) throw error;
    },
    onSuccess: (_data, apartmentId) => {
      qc.invalidateQueries({
        queryKey: QK.apartmentBankOverride(apartmentId),
      });
    },
  });
}

// ─── Bookings (used by Room + Tenant detail screens) ──────────────────────

export type BookingRow = {
  id: number;
  user_id: string | null;
  room_id: number;
  status: string;
  start_date: string;
  end_date: string;
  // Contact stored directly on booking (used when no auth user is linked)
  name: string | null;
  email: string | null;
  phone: string | null;
  description: string | null;
  monthly_rent: number | null;
  deposit_amount: number | null;
  first_month_rent_override: number | null;
  move_in_date: string | null;
  move_out_date: string | null;
  actual_move_in_date: string | null;
  actual_move_out_date: string | null;
  contract_status: string | null;
  contract_signed_at: string | null;
  manual_contract_upload: boolean | null;
  docusign_status: string | null;
  docusign_envelope_id: string | null;
  contract_pdf_url: string | null;
  signed_contract_url: string | null;
  contract_url: string | null;
  payment_status: string | null;
  notes: string | null;
  // Key handover tracking (20260718120000_booking_key_handover_tracking).
  // Drive the "Key pickup pending" / "Key return pending" journey stages.
  key_picked_up_at: string | null;
  key_returned_at: string | null;
  // Journey evidence (20260731100000_tenant_journey_evidence_columns). The
  // Active / Move-out scheduled gates read these timestamps, never `status`.
  move_in_confirmed_at: string | null;
  move_out_notice_at: string | null;
  // Protocol reminder / auto-accept tracking (20260521120000 / 20260521140000).
  // Idempotency timestamps for the move-in / move-out email sequences.
  move_in_activation_email_sent_at: string | null;
  move_in_reminder_24h_sent_at: string | null;
  move_in_reminder_36h_sent_at: string | null;
  move_in_auto_accepted_at: string | null;
  move_out_reminder_48h_sent_at: string | null;
  move_out_reminder_36h_sent_at: string | null;
  move_out_reminder_12h_sent_at: string | null;
  move_out_auto_completed_at: string | null;
  // Admin "reopen protocol for tenant" grace stamps (COL-424). Kind-scoped —
  // reopening move-in never touches move_out_protocol_reopened_at.
  move_in_protocol_reopened_at: string | null;
  move_out_protocol_reopened_at: string | null;
  /**
   * When set, an admin has archived the booking (hidden from the main pipeline
   * lists). This is the real DB column — there is no `is_archived` boolean.
   */
  admin_archived_at: string | null;
  /**
   * Why it was archived, when the SYSTEM archived it (COL-165): 'room_filled'
   * (someone else signed for this room) or 'signed_elsewhere' (this applicant
   * signed for another one). NULL means an admin did it by hand — which is
   * every row archived before COL-165.
   */
  admin_archive_reason: string | null;
  // Tenant discount (COL-360). Value NULL = no discount on that side; a NULL
  // rent_discount_months with a rent value means it runs for the full term.
  rent_discount_mode: string | null;
  rent_discount_value: number | null;
  rent_discount_months: number | null;
  rent_discount_start_month: string | null;
  deposit_discount_mode: string | null;
  deposit_discount_value: number | null;
  /**
   * COL-618: the applicant's answer to "Does the Jobcenter pay your rent?".
   * NULL = never asked (the booking predates the question), distinct from false.
   */
  jobcenter_pays_rent: boolean | null;
  created_at: string;
  updated_at: string;
};

/** Booking lifecycle states that count as actively occupying a room. */
export const ACTIVE_BOOKING_STATUSES = [
  "confirmed",
  "active",
  "approved",
] as const;

/**
 * Canonical "is this booking archived" test. An admin archives a booking by
 * stamping `admin_archived_at`; the Tenants pipeline, room occupancy and this
 * predicate all key off that single column.
 */
export function isBookingArchived(
  b: Pick<BookingRow, "admin_archived_at">
): boolean {
  return b.admin_archived_at != null;
}

/**
 * Canonical "is this booking active" test — the single source of truth for
 * room occupancy. A non-archived booking in a live lifecycle state. This is
 * the same predicate the reassignment engine and the single-occupancy RPC
 * enforce; `room.status` / `room.available` are NOT authoritative (they're
 * manual / payment-driven and can drift), so occupancy badges and counts must
 * be derived from this instead.
 */
export function isActiveBooking(
  b: Pick<BookingRow, "status" | "admin_archived_at">
): boolean {
  return (
    !isBookingArchived(b) &&
    (ACTIVE_BOOKING_STATUSES as readonly string[]).includes(b.status)
  );
}

/**
 * Canonical "has this booking become a real tenancy" test. A booking crosses
 * the line from *application* to *tenant* when the contract is signed (either
 * `contract_signed_at` is stamped, or `contract_status` reached an accepted
 * state). This mirrors the Tenants pipeline's bucketing — it's intentionally
 * shared so the room history and the pipeline can't disagree about who counts
 * as a tenant. (Historically this predicate was copy-pasted into AdminTenants
 * and Dashboard; new code should import this one.)
 */
export function isBookingContractSigned(
  b:
    | Pick<BookingRow, "contract_signed_at" | "contract_status">
    | null
    | undefined
): boolean {
  if (!b) return false;
  if (b.contract_signed_at) return true;
  const cs = b.contract_status;
  return cs === "signed" || cs === "approved";
}

/** Effective move-in date: the actual move-in if known, else the planned
 *  move-in, else the booking's nominal start. Never null (start_date is). */
export function effectiveStartDate(
  b: Pick<BookingRow, "actual_move_in_date" | "move_in_date" | "start_date">
): string {
  return b.actual_move_in_date ?? b.move_in_date ?? b.start_date;
}

/** Effective move-out date: the actual move-out if known, else the planned
 *  move-out, else the booking's nominal end. May be null. */
export function effectiveEndDate(
  b: Pick<BookingRow, "actual_move_out_date" | "move_out_date" | "end_date">
): string | null {
  return b.actual_move_out_date ?? b.move_out_date ?? b.end_date ?? null;
}

/**
 * Canonical "is someone occupying this tenancy RIGHT NOW" test — the gate every
 * rent-collection surface shares (AdminReminders, the property Reminders tab,
 * and the `dunning-tick` edge function, which keeps a byte-equivalent copy in
 * `supabase/functions/_shared/dunning.ts`).
 *
 * It is {@link isActiveBooking} (live status, not admin-archived) windowed by
 * the *effective* tenancy dates, and deliberately nothing else — so a tenant
 * who has moved out or hasn't moved in yet stops surfacing as a liability.
 *
 * Notably absent: `bookings.tenant_active`. That column backed a per-row
 * Active/Inactive toggle on the Reminders rows, removed in PR #1012 after
 * admins flipped it by accident. The column outlived the only UI that could
 * set OR unset it, so a stray `false` became permanent and unreachable — and
 * because the lists AND the tick both excluded on it, the tenant dropped out
 * of rent collection entirely, with no row, no badge and no log to say so
 * (booking 302, a signed and fully-paid tenant, found 2026-09-06). Occupancy
 * must be derivable from tenancy facts, never from a hidden flag.
 */
export function isOccupyingNow(
  b: Pick<
    BookingRow,
    | "status"
    | "admin_archived_at"
    | "actual_move_in_date"
    | "move_in_date"
    | "start_date"
    | "actual_move_out_date"
    | "move_out_date"
    | "end_date"
  >,
  now: Date = new Date()
): boolean {
  if (!isActiveBooking(b)) return false;
  const nowMs = now.getTime();
  const startStr = effectiveStartDate(b);
  // NOT effectiveEndDate: that falls back to end_date, which is the earliest
  // POSSIBLE move-out (contracts auto-prolong), not a real one. Honouring it
  // dropped tenants out of Reminders and dunning the month their term rolled
  // past, silently. Only a confirmed departure ends a tenancy here — same
  // anchor the move-out emails were moved off in #1066/#1067 and billing in
  // rent-materialize.ts. effectiveEndDate keeps its end_date fallback for room
  // lifecycle/history, where "earliest possible" is the right bound.
  const endStr = b.actual_move_out_date ?? b.move_out_date;
  const startMs = startStr ? Date.parse(startStr) : null;
  const endMs = endStr ? Date.parse(endStr) : null;
  if (startMs !== null && startMs > nowMs) return false;
  if (endMs !== null && endMs < nowMs) return false;
  return true;
}

/**
 * Statuses of a tenant who has started move-out: `notice_given` (notice
 * recorded) and `move_out_pending` (move-out date confirmed). Neither is in
 * {@link ACTIVE_BOOKING_STATUSES} — "who owes rent" and "who lives here" are
 * different questions — but until the tenant actually leaves, they still live
 * in the room. (`move_out_pending` is deliberately not terminal in
 * `bookings_status_check`: notice is given but the tenant is still there.)
 */
const NOTICE_PERIOD_STATUSES: readonly string[] = [
  "notice_given",
  "move_out_pending",
];

/**
 * Has this tenant physically left? Keys handed back, or the confirmed move-out
 * day (actual, else planned) is behind us. The move-out day itself still counts
 * as living there. Never `end_date`: that is the earliest POSSIBLE move-out
 * (contracts auto-prolong), not a departure — see {@link isOccupyingNow}. No
 * confirmed date and no key return means they have not left.
 */
function hasLeftRoom(
  b: Pick<
    BookingRow,
    "key_returned_at" | "actual_move_out_date" | "move_out_date"
  >,
  now: Date
): boolean {
  if (b.key_returned_at) return true;
  const out = b.actual_move_out_date ?? b.move_out_date;
  if (!out) return false;
  const day = parseDateOnly(out);
  const dayAfter = new Date(
    day.getFullYear(),
    day.getMonth(),
    day.getDate() + 1
  );
  // An unparseable date is NaN, so this is false: still treated as living there.
  return dayAfter.getTime() <= now.getTime();
}

/**
 * Where a booking sits in a room's occupancy, from the booking row alone.
 *
 *   - "current"  → someone living in the room *now*: an active booking whose
 *                  move-in has happened, or a tenant in their notice period
 *                  (`notice_given` / `move_out_pending`) who has not left yet
 *   - "upcoming" → an active (committed) booking that hasn't started yet
 *   - "ended"    → everything else: left, archived, cancelled, never started
 *
 * Needs no payment evidence, so the Current tenant tab and the room's Occupied
 * badge use it directly. Whether an "ended" booking was ever a real tenancy is
 * a separate question — see {@link classifyRoomBooking}.
 */
export type RoomOccupancyPhase = "current" | "upcoming" | "ended";

export function roomOccupancyPhase(
  b: Pick<
    BookingRow,
    | "status"
    | "admin_archived_at"
    | "start_date"
    | "move_in_date"
    | "actual_move_in_date"
    | "move_out_date"
    | "actual_move_out_date"
    | "key_returned_at"
  >,
  now: Date = new Date()
): RoomOccupancyPhase {
  const inNoticePeriod =
    !isBookingArchived(b) && NOTICE_PERIOD_STATUSES.includes(b.status);
  const living = isActiveBooking(b) || (inNoticePeriod && !hasLeftRoom(b, now));
  if (!living) return "ended";
  // Split by effective start so "current" and "upcoming" never overlap (and
  // never double-display).
  const start = effectiveStartDate(b);
  const startTs = start ? new Date(start).getTime() : NaN;
  return Number.isFinite(startTs) && startTs > now.getTime()
    ? "upcoming"
    : "current";
}

/**
 * Occupancy counts on property/apartment cards and lists are derived from
 * bookings, so every booking mutation that can change who lives where must
 * refresh them along with the booking queries.
 */
export function invalidateOccupancyViews(qc: QueryClient): Promise<void> {
  // Cards refresh in the background; callers that await get the same promise
  // they always did (the booking queries), not the heavier list refetches.
  void qc.invalidateQueries({ queryKey: ["v2", "properties", "list"] });
  void qc.invalidateQueries({ queryKey: ["v2", "apartments"] });
  return qc.invalidateQueries({ queryKey: ["admin-all-bookings"] });
}

/** Columns {@link roomOccupancyPhase} reads, plus the room the row belongs to. */
const OCCUPANCY_BOOKING_COLUMNS =
  "room_id, status, admin_archived_at, start_date, move_in_date, actual_move_in_date, move_out_date, actual_move_out_date, key_returned_at";

/** Statuses worth reading: everything {@link roomOccupancyPhase} can call occupied. */
const OCCUPANCY_BOOKING_STATUSES = [
  ...ACTIVE_BOOKING_STATUSES,
  ...NOTICE_PERIOD_STATUSES,
];

/**
 * THE source of truth for "is this room occupied" in the admin UI: a room is
 * occupied when {@link roomOccupancyPhase} says some booking is living there,
 * about to live there, or in its notice period. `rooms.status` /
 * `rooms.available` are NOT consulted — they go stale (a tenant leaves and
 * nobody flags the room free), which is how apartment cards read "5/5
 * occupied" while the room list showed a vacant room. The website listing
 * (`available_from`, derived from the same bookings) and the twice-monthly
 * vacancy email agree with this rule; the flag is the odd one out.
 */
export function occupiedRoomIdsFromBookings(
  rows: Array<
    Omit<Parameters<typeof roomOccupancyPhase>[0], never> & {
      room_id: number | string;
    }
  >,
  now: Date = new Date()
): Set<number> {
  const ids = new Set<number>();
  for (const b of rows) {
    if (roomOccupancyPhase(b, now) !== "ended") ids.add(Number(b.room_id));
  }
  return ids;
}

/** Occupied room ids across one property (admin-only: reads bookings). */
async function fetchPropertyOccupiedRoomIds(
  propertyId: number
): Promise<Set<number>> {
  // Paged: an unpaged select is cut at PostgREST's row cap without a word, and
  // a missing booking reads as a vacant room.
  const PAGE = 1000;
  const rows: Parameters<typeof occupiedRoomIdsFromBookings>[0] = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("bookings")
      .select(`id, ${OCCUPANCY_BOOKING_COLUMNS}, rooms!inner(property_id)`)
      .eq("rooms.property_id", propertyId)
      .is("admin_archived_at", null)
      .in("status", OCCUPANCY_BOOKING_STATUSES)
      .order("id", { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) throw error;
    rows.push(...(data ?? []));
    if ((data ?? []).length < PAGE) break;
  }
  return occupiedRoomIdsFromBookings(rows);
}

/** Occupied room ids among `roomIds`, chunked and paged for portfolio lists. */
async function fetchOccupiedRoomIds(
  roomIds: Array<number | string>
): Promise<Set<number>> {
  const rows = await fetchAllByColumn<
    Parameters<typeof occupiedRoomIdsFromBookings>[0][number]
  >(
    "bookings",
    OCCUPANCY_BOOKING_COLUMNS,
    "room_id",
    roomIds,
    "id",
    true,
    (q) =>
      q.is("admin_archived_at", null).in("status", OCCUPANCY_BOOKING_STATUSES)
  );
  return occupiedRoomIdsFromBookings(rows);
}

/**
 * Room ids in a property that are occupied by the same rule the Room detail
 * page uses ({@link roomOccupancyPhase}). The reassign occupant map only holds
 * ACTIVE_BOOKING_STATUSES, so it reads a room whose tenant has a confirmed
 * move-out date as vacant.
 */
export function usePropertyOccupiedRoomIds(
  propertyId: number | null | undefined
) {
  return useQuery({
    queryKey: ["admin-all-bookings", "property-occupied-rooms", propertyId],
    enabled: !!propertyId,
    queryFn: async (): Promise<Set<number>> =>
      propertyId ? fetchPropertyOccupiedRoomIds(propertyId) : new Set<number>(),
  });
}

/**
 * Where a booking sits in a room's tenancy history. The Room detail page — its
 * occupancy badge, the Current tenant tab and the History tab — all derive from
 * {@link roomOccupancyPhase} plus this function, so they can never contradict
 * each other.
 *
 *   - "current"     → living in the room now (incl. notice period)
 *   - "upcoming"    → an active (committed) booking that hasn't started yet
 *   - "past"        → a tenancy that actually happened and has concluded
 *   - "application" → ended without ever becoming a tenancy, but still on file
 *                     (pending / withdrawn / cancelled)
 *   - "archived"    → an admin retired a booking that never became a tenancy;
 *                     it belongs on no list of this room
 *
 * A booking only counts as a real tenancy (`realTenancy`, see
 * `bookingWasRealTenancy`) once a move-in was recorded or its deposit was paid —
 * the commitment that takes the room off the market. Applicants who did
 * neither were never in the room, so they are not "past tenants" — archived or
 * not. An archived booking that DID become a tenancy and moved out stays a past
 * tenant; the archive flag alone never erases a real tenancy.
 *
 * Occupancy app-wide stays keyed off {@link isActiveBooking}; "current" and
 * "upcoming" are that active set (plus tenants in notice) split by move-in
 * date, so a room with a committed future tenant still reads as occupied.
 */
export type RoomBookingPhase =
  | "current"
  | "upcoming"
  | "past"
  | "application"
  | "archived";

export function classifyRoomBooking(
  b: Parameters<typeof roomOccupancyPhase>[0],
  now: Date,
  realTenancy: boolean
): RoomBookingPhase {
  const occupancy = roomOccupancyPhase(b, now);
  if (occupancy !== "ended") return occupancy;
  if (realTenancy) return "past";
  return isBookingArchived(b) ? "archived" : "application";
}

/** Apartment-level occupancy derived from its rooms' active bookings. */
export type ApartmentOccupancy = "vacant" | "partial" | "occupied";

/**
 * Derive an apartment's occupancy from how many of its (visible) rooms have an
 * active booking — the same single source of truth as the room badges. Use
 * this instead of the manual, drift-prone `apartments.rental_status` field.
 */
export function deriveApartmentOccupancy(
  occupiedRooms: number,
  totalVisibleRooms: number
): ApartmentOccupancy {
  if (totalVisibleRooms === 0 || occupiedRooms === 0) return "vacant";
  if (occupiedRooms >= totalVisibleRooms) return "occupied";
  return "partial";
}

export type BookingWithRoom = BookingRow & {
  room_title: string | null;
  room_number: string | null;
  property_title: string | null;
};

export function useBookingsForUser(userId: string | null | undefined) {
  return useQuery({
    queryKey: userId
      ? ["v2", "bookings-for-user", userId]
      : ["v2", "bookings-for-user", "noop"],
    queryFn: async (): Promise<BookingWithRoom[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("bookings")
        .select("*, rooms(title, room_number, properties(title))")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return ((data ?? []) as any[]).map((b) => ({
        ...b,
        room_title: b.rooms?.title ?? null,
        room_number: b.rooms?.room_number ?? null,
        property_title: b.rooms?.properties?.title ?? null,
      })) as BookingWithRoom[];
    },
    enabled: !!userId,
  });
}

export function useBookingsForRoom(roomId: number | null | undefined) {
  return useQuery({
    queryKey: roomId ? QK.bookings(roomId) : ["v2", "bookings", "noop"],
    queryFn: async (): Promise<BookingRow[]> => {
      if (!roomId) return [];
      const { data, error } = await supabase
        .from("bookings")
        .select("*")
        .eq("room_id", roomId)
        .order("start_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as BookingRow[];
    },
    enabled: !!roomId,
  });
}

export function useBooking(id: number | null | undefined) {
  return useQuery({
    queryKey: id ? QK.booking(id) : ["v2", "bookings", "noop"],
    queryFn: async (): Promise<BookingRow | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("bookings")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as BookingRow | null;
    },
    enabled: !!id,
  });
}

// ─── Audit logs (tenant detail timeline) ──────────────────────────────────

export type AuditLogRow = {
  id: string;
  user_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string;
  old_values: unknown;
  new_values: unknown;
  ip_address: string | null;
  user_agent: string | null;
  metadata: unknown;
  created_at: string;
  /** Actor profile embedded via user_id, for the "by <name>" line. */
  actor?: { id: string; name: string | null; email: string | null } | null;
};

export function useAuditLogsForBooking(bookingId: number | null | undefined) {
  return useQuery({
    queryKey: bookingId
      ? QK.auditLogs("booking", String(bookingId))
      : ["v2", "audit-logs", "noop"],
    queryFn: async (): Promise<AuditLogRow[]> => {
      if (!bookingId) return [];
      // `audit_logs.user_id` FKs into auth.users, which PostgREST cannot embed
      // (unexposed schema) — select the plain columns and hydrate actor
      // profiles separately, same pattern as useAdminNotes below.
      const sel =
        "id, user_id, action, resource_type, resource_id, old_values, new_values, ip_address, user_agent, metadata, created_at";
      // (a) Booking-scoped edits (super-admin manual overrides), plus
      // (b) anything tagged with this booking in metadata — e.g. payment edits
      // logged under the payment resource. (b) is best-effort: if the jsonb
      // filter isn't supported we still return the booking-scoped rows.
      const byResource = await supabase
        .from("audit_logs")
        .select(sel)
        .eq("resource_type", "booking")
        .eq("resource_id", String(bookingId))
        .order("created_at", { ascending: false })
        .limit(100);
      if (byResource.error) throw byResource.error;

      let metaRows: AuditLogRow[] = [];
      try {
        const byMeta = await supabase
          .from("audit_logs")
          .select(sel)
          .eq("metadata->>booking_id", String(bookingId))
          .order("created_at", { ascending: false })
          .limit(100);
        if (!byMeta.error && byMeta.data)
          metaRows = byMeta.data as AuditLogRow[];
      } catch {
        /* jsonb filter unsupported — booking-scoped rows still surface */
      }

      const merged = new Map<string, AuditLogRow>();
      for (const r of [
        ...((byResource.data ?? []) as AuditLogRow[]),
        ...metaRows,
      ]) {
        merged.set(r.id, r);
      }
      const rows = Array.from(merged.values()).sort((a, b) =>
        (b.created_at ?? "").localeCompare(a.created_at ?? "")
      );

      const actorIds = Array.from(
        new Set(rows.map((r) => r.user_id).filter(Boolean) as string[])
      );
      if (actorIds.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", actorIds);
        const byId = new Map(
          (
            (profs ?? []) as Array<{
              id: string;
              name: string | null;
              email: string | null;
            }>
          ).map((p) => [p.id, p])
        );
        for (const r of rows) {
          r.actor = r.user_id ? (byId.get(r.user_id) ?? null) : null;
        }
      }
      return rows;
    },
    enabled: !!bookingId,
  });
}

// ─── Profile lookup (tenant detail header) ────────────────────────────────

export type ProfileRow = {
  id: string;
  email: string | null;
  name: string | null;
  /** Main lineage uses full_name; kept for cross-branch compatibility. */
  full_name: string | null;
  phone: string | null;
  date_of_birth: string | null;
  /** 'manual' when a person typed it in, 'vision' when extracted from an ID. */
  date_of_birth_source: string | null;
  /** COL-530: 'female' | 'male' | null. Published on the public room page. */
  gender: string | null;
  /** COL-618: ISO 3166-1 alpha-2 code | null. Admin-only — never published. */
  nationality: string | null;
};

/** Everything the profile editor reads, minus the newest column. */
const PROFILE_COLUMNS =
  "id, email, name, full_name, phone, date_of_birth, date_of_birth_source, gender";

export function useProfile(userId: string | null | undefined) {
  return useQuery({
    queryKey: userId ? ["v2", "profile", userId] : ["v2", "profile", "noop"],
    queryFn: async (): Promise<ProfileRow | null> => {
      if (!userId) return null;
      // A column list built at run time defeats the client's select-string
      // typing, so the row is typed by hand here.
      const read = async (columns: string) => {
        const { data, error } = await supabase
          .from("profiles")
          .select(columns)
          .eq("id", userId)
          .maybeSingle();
        return {
          data: data as unknown as
            | (Omit<ProfileRow, "nationality"> & {
                nationality?: string | null;
              })
            | null,
          error,
        };
      };
      const full = await read(`${PROFILE_COLUMNS}, nationality`);
      if (!full.error) return (full.data ?? null) as ProfileRow | null;
      // PostgREST rejects the WHOLE select on one unknown column (42703). A
      // database that has not yet run 20261105090000 has no `nationality`, and
      // losing the whole profile — phone, date of birth, gender — for want of
      // one new column would blank the tenant's identity block. Ask again
      // without it; nationality then reads as unknown.
      if (full.error.code !== "42703") throw full.error;
      const legacy = await read(PROFILE_COLUMNS);
      if (legacy.error) throw legacy.error;
      return legacy.data ? { ...legacy.data, nationality: null } : null;
    },
    enabled: !!userId,
  });
}

// ─── Mutations (super-admin edits on tenant detail) ───────────────────────

type BookingPatch = Partial<
  Pick<
    BookingRow,
    | "status"
    | "contract_status"
    | "payment_status"
    | "move_in_date"
    | "move_out_date"
    | "actual_move_in_date"
    | "actual_move_out_date"
    | "move_in_confirmed_at"
    | "start_date"
    | "end_date"
    | "monthly_rent"
    | "deposit_amount"
    | "name"
    | "email"
    | "phone"
    | "notes"
    | "jobcenter_pays_rent"
  >
>;

export function useUpdateBooking(bookingId: number | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: BookingPatch) => {
      if (!bookingId) throw new Error("No booking id");
      const { error } = await supabase
        .from("bookings")
        .update({ ...patch, updated_at: new Date().toISOString() })
        .eq("id", bookingId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.booking(bookingId!) });
      invalidateOccupancyViews(qc);
    },
  });
}

// ─── Tenant documents ────────────────────────────────────────────────────────

export type TenantDocumentRow = {
  id: string;
  user_id: string | null;
  booking_id: number | null;
  file_url: string;
  file_name: string | null;
  document_type: string;
  title: string;
  status: string;
  notes: string | null;
  created_at: string;
  /** When `notes` was last saved (20260731100000_tenant_journey_evidence_columns). */
  notes_updated_at: string | null;
  notes_updated_by: string | null;
  /** Hydrated from profiles — null for notes written before the column existed. */
  notes_author?: {
    id: string;
    name: string | null;
    email: string | null;
  } | null;
};

export function useTenantDocuments(
  userId: string | null | undefined,
  bookingId: number | null | undefined
) {
  return useQuery({
    queryKey: ["v2", "tenant-documents", userId ?? null, bookingId ?? null],
    queryFn: async (): Promise<TenantDocumentRow[]> => {
      const results: TenantDocumentRow[] = [];
      const seen = new Set<string>();
      if (userId) {
        const { data, error } = await supabase
          .from("tenant_documents")
          .select(
            "id, user_id, booking_id, file_url, file_name, document_type, title, status, notes, notes_updated_at, notes_updated_by, created_at"
          )
          .eq("user_id", userId)
          .order("created_at", { ascending: false });
        if (error) throw error;
        for (const r of (data ?? []) as TenantDocumentRow[]) {
          if (!seen.has(r.id)) {
            seen.add(r.id);
            results.push(r);
          }
        }
      }
      if (bookingId) {
        const { data, error } = await supabase
          .from("tenant_documents")
          .select(
            "id, user_id, booking_id, file_url, file_name, document_type, title, status, notes, notes_updated_at, notes_updated_by, created_at"
          )
          .eq("booking_id", bookingId)
          .order("created_at", { ascending: false });
        if (error) throw error;
        for (const r of (data ?? []) as TenantDocumentRow[]) {
          if (!seen.has(r.id)) {
            seen.add(r.id);
            results.push(r);
          }
        }
      }
      // Hydrate note authors — `notes_updated_by` references auth.users, which
      // PostgREST cannot embed, so resolve the profiles separately (same shape
      // as useAdminNotes / useAuditLogsForBooking).
      const authorIds = Array.from(
        new Set(
          results.map((r) => r.notes_updated_by).filter(Boolean) as string[]
        )
      );
      if (authorIds.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("id, name, email")
          .in("id", authorIds);
        const byId = new Map(
          (
            (profs ?? []) as Array<{
              id: string;
              name: string | null;
              email: string | null;
            }>
          ).map((p) => [p.id, p])
        );
        for (const r of results) {
          r.notes_author = r.notes_updated_by
            ? (byId.get(r.notes_updated_by) ?? null)
            : null;
        }
      }
      return results;
    },
    enabled: !!(userId || bookingId),
  });
}

// ─── Tenant issues (support tickets) ─────────────────────────────────────────

export type IssueRow = {
  id: string;
  title: string;
  description: string | null;
  status: string;
  issue_type: string | null;
  created_at: string;
};

export function useTenantIssues(userId: string | null | undefined) {
  return useQuery({
    queryKey: ["v2", "tenant-issues", userId ?? null],
    queryFn: async (): Promise<IssueRow[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("issues")
        .select("id, title, description, status, issue_type, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as IssueRow[];
    },
    enabled: !!userId,
  });
}

// ─── Booking payments ─────────────────────────────────────────────────────────

export type PaymentRow = {
  id: string;
  booking_id: number | null;
  type: "rent" | "deposit" | string;
  amount_cents: number;
  /**
   * Money actually received against this row, maintained by the
   * `payment_receipts` trigger (COL-417). Without it `resolveRentSettlement`
   * reads every open month as untouched, so a part-paid month renders its full
   * bill and the partial UI never appears — the read-side gap this select was
   * missed by in #1468.
   */
  received_cents: number | null;
  /**
   * The full rent when a coliviq-funded kickback or referral credit lowered
   * `amount_cents` (COL-621, src/lib/rent-kickback.ts). Read so the rent editor's
   * "Kickback" checkbox and chip reflect the stored row.
   */
  gross_amount_cents: number | null;
  currency: string | null;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  /**
   * Admin attestation that this row was settled off-platform
   * (20260802110000_journey_evidence_followups). What makes a manual "paid" row
   * count in `isPaymentEffectivelyPaid` when there is no uploadable proof.
   */
  marked_paid_at: string | null;
  marked_paid_by: string | null;
  description: string | null;
  created_at: string;
};

export function useBookingPayments(bookingId: number | null | undefined) {
  return useQuery({
    queryKey: ["v2", "payments", "by-booking", bookingId ?? null],
    queryFn: async (): Promise<PaymentRow[]> => {
      if (!bookingId) return [];
      const { data, error } = await supabase
        .from("payments")
        .select(
          "id, booking_id, type, amount_cents, received_cents, gross_amount_cents, currency, status, due_date, paid_at, stripe_payment_intent_id, stripe_charge_id, marked_paid_at, marked_paid_by, description, created_at"
        )
        .eq("booking_id", bookingId)
        .in("type", ["rent", "deposit"])
        .order("due_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as PaymentRow[];
    },
    enabled: !!bookingId,
  });
}

// ─── Admin notes + attachments (COL-245) ──────────────────────────────────
// Internal operator notes scoped to a tenant (booking/user) and/or a specific
// payment. Admin-only (RLS). Each note may carry text and/or one file.

export type AdminNoteRow = {
  id: string;
  booking_id: number | null;
  user_id: string | null;
  payment_id: string | null;
  body: string | null;
  file_url: string | null;
  file_name: string | null;
  file_size: number | null;
  mime_type: string | null;
  created_by: string | null;
  created_at: string;
  /**
   * Which internal surface the note belongs to. null/'general' is the ordinary
   * tenant notes feed; 'contract_internal' is the admin-only note on the
   * contract card (super_admin write only, enforced by RLS).
   */
  kind: string | null;
  /**
   * Threading and lifecycle, written by the notes drawer (useEntityNotes) and
   * read here so the Activity Timeline and the Documents tab agree with it —
   * a reply is not a second note, a retracted note is gone, and a resolved
   * thread says so.
   *
   * All five arrive with 20260812100000_admin_notes_entity_scope_threads and
   * read as null on a database that predates it. Null therefore means "not a
   * reply / never edited / open / live", which is what every row on such a
   * database in fact is: nothing could have written the other states.
   */
  parent_id: string | null;
  edited_at: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  deleted_at: string | null;
  author?: { id: string; name: string | null; email: string | null } | null;
  /** Who resolved the thread, hydrated alongside `author`. */
  resolver?: { id: string; name: string | null; email: string | null } | null;
};

/** `kind` of the admin-only note shown on a tenant's contract card. */
export const CONTRACT_INTERNAL_NOTE_KIND = "contract_internal";

export type AdminNoteScope = {
  bookingId?: number | null;
  userId?: string | null;
  paymentId?: string | null;
};

const adminNotesKey = (scope: AdminNoteScope) =>
  [
    "v2",
    "admin-notes",
    scope.paymentId ?? null,
    scope.bookingId ?? null,
    scope.userId ?? null,
  ] as const;

const ADMIN_NOTE_COLS =
  "id, booking_id, user_id, payment_id, body, file_url, file_name, file_size, mime_type, created_by, created_at";

/**
 * How much of `admin_notes` this database has.
 *
 * The tiers are NESTED, not independent: `kind` arrives with 20260804091500 and
 * the threading columns with 20260812100000, so anything with the latter has
 * the former. That is what lets one probe answer for both — walk down from the
 * widest column list until a SELECT succeeds, rather than testing each column.
 */
type AdminNoteTier = "threads" | "kind" | "base";

/** Threading + lifecycle — 20260812100000_admin_notes_entity_scope_threads. */
const ADMIN_NOTE_THREAD_COLS =
  "parent_id, edited_at, resolved_at, resolved_by, deleted_at";

const TIER_COLS: Record<AdminNoteTier, string> = {
  threads: `${ADMIN_NOTE_COLS}, kind, ${ADMIN_NOTE_THREAD_COLS}`,
  kind: `${ADMIN_NOTE_COLS}, kind`,
  base: ADMIN_NOTE_COLS,
};

/** Widest first — the order the probe walks. */
const TIER_ORDER: AdminNoteTier[] = ["threads", "kind", "base"];

/**
 * Which tier this database answered at. Remembered for the session.
 *
 * The probe costs a 400 per missing tier, and every notes surface runs it — the
 * tenant feed, the timeline, the per-payment note counts, each card. On an
 * environment that is a migration behind, that is a red console error per
 * query, repeated on every refetch, for a condition already known after the
 * first one.
 *
 * Only a NARROWED answer expires: it stops being true the moment `supabase db
 * push` runs, and the page should notice without a hard reload (same 60s window
 * as useContractFlowReady). The widest tier can never become false.
 */
let noteTierSupport: { tier: AdminNoteTier; at: number } | null = null;
const TIER_SUPPORT_TTL_MS = 60_000;

function knownNoteTier(): AdminNoteTier | null {
  if (!noteTierSupport) return null;
  if (noteTierSupport.tier === "threads") return "threads";
  return Date.now() - noteTierSupport.at < TIER_SUPPORT_TTL_MS
    ? noteTierSupport.tier
    : null;
}

/** Reset by tests, and after a schema change is applied in-session. */
export function resetAdminNotesSchemaSupport() {
  noteTierSupport = null;
}

/**
 * Fill in the columns this tier could not ask for.
 *
 * Null across the board, which reads correctly: on a database without the
 * threading columns no row can be a reply, retracted or resolved, because
 * nothing exists to write those states.
 */
function toAdminNoteRows(data: unknown, tier: AdminNoteTier): AdminNoteRow[] {
  const raw = (data ?? []) as Array<Record<string, unknown>>;
  return raw.map((r) => ({
    ...(r as unknown as AdminNoteRow),
    kind: tier === "base" ? null : ((r.kind as string | null) ?? null),
    parent_id:
      tier === "threads" ? ((r.parent_id as string | null) ?? null) : null,
    edited_at:
      tier === "threads" ? ((r.edited_at as string | null) ?? null) : null,
    resolved_at:
      tier === "threads" ? ((r.resolved_at as string | null) ?? null) : null,
    resolved_by:
      tier === "threads" ? ((r.resolved_by as string | null) ?? null) : null,
    deleted_at:
      tier === "threads" ? ((r.deleted_at as string | null) ?? null) : null,
  }));
}

/**
 * One `admin_notes` read, at the widest column list this database supports.
 *
 * Selecting a column a database does not have fails the WHOLE query (PostgREST
 * 42703), which would blank every notes surface on an environment that is a
 * migration behind — so ask for the most, and step down on 42703 alone.
 *
 * Retracted rows are dropped here rather than in each caller: a note somebody
 * deleted in the notes drawer should exist on no surface, and a filter every
 * reader has to remember is one a future reader will forget.
 */
async function selectAdminNotes(build: (query: any) => any): Promise<{
  rows: AdminNoteRow[];
  hasKind: boolean;
  /** False when `resolved_at` / `parent_id` / `deleted_at` are unknowable. */
  hasThreads: boolean;
}> {
  // Already established how far this database goes — start there, don't
  // re-probe the tiers it has already refused.
  const start = knownNoteTier() ?? TIER_ORDER[0];

  for (let i = TIER_ORDER.indexOf(start); i < TIER_ORDER.length; i++) {
    const tier = TIER_ORDER[i];
    const res = await build(
      supabase.from("admin_notes").select(TIER_COLS[tier])
    );

    if (!res.error) {
      noteTierSupport = { tier, at: Date.now() };
      return {
        rows: toAdminNoteRows(res.data, tier).filter((r) => !r.deleted_at),
        hasKind: tier !== "base",
        hasThreads: tier === "threads",
      };
    }

    // ONLY a missing column justifies stepping down. Falling back on any error
    // meant a transient failure reported hasKind = false, and
    // useContractInternalNote then answered "there is no note" for a booking
    // that has one — a wrong answer is worse than a visible error. For the same
    // reason a transient failure must not be remembered as a narrower tier.
    const isLast = i === TIER_ORDER.length - 1;
    if (isLast || !isMissingColumn(res.error)) throw res.error;
  }

  // Unreachable: the loop returns or throws on the last tier, whose columns
  // have existed for as long as the table has.
  throw new Error("admin_notes: no supported column set.");
}

/**
 * Hydrate `author` and `resolver` from profiles — both reference auth.users,
 * which PostgREST cannot join to, so they are one extra round trip. Resolved by
 * a bare uuid is not something to print at an operator, and "Resolved" with no
 * name at all invites the question this answers.
 */
async function hydrateNoteAuthors(
  rows: AdminNoteRow[]
): Promise<AdminNoteRow[]> {
  const ids = Array.from(
    new Set(
      rows.flatMap((r) => [r.created_by, r.resolved_by]).filter(Boolean)
    ) as Set<string>
  );
  if (!ids.length) return rows;
  const { data: profs } = await supabase
    .from("profiles")
    .select("id, name, email")
    .in("id", ids);
  const byId = new Map(
    (
      (profs ?? []) as Array<{
        id: string;
        name: string | null;
        email: string | null;
      }>
    ).map((p) => [p.id, p])
  );
  for (const r of rows) {
    r.author = r.created_by ? (byId.get(r.created_by) ?? null) : null;
    r.resolver = r.resolved_by ? (byId.get(r.resolved_by) ?? null) : null;
  }
  return rows;
}

/**
 * Chronological admin notes for a scope. When paymentId is set we return that
 * payment's notes; otherwise the tenant's notes (matched by booking_id OR
 * user_id). Newest first. Author profiles are hydrated separately because
 * created_by references auth.users, not public.profiles.
 *
 * Contract internal notes (`kind = 'contract_internal'`) are excluded: they have
 * their own surface on the contract card, and listing them here would show the
 * same text twice in the profile and in the timeline.
 *
 * Notes retracted in the notes drawer are excluded too — `selectAdminNotes`
 * drops them for every reader. Replies are NOT: they come back as ordinary rows
 * carrying `parent_id`, and it is each surface's business whether to fold them
 * into their root (the Activity Timeline) or ignore the field.
 */
export async function fetchAdminNotes(
  scope: AdminNoteScope
): Promise<AdminNoteRow[]> {
  const { rows } = await selectAdminNotes((q) => {
    let query = q.order("created_at", { ascending: false });
    if (scope.paymentId) {
      query = query.eq("payment_id", scope.paymentId);
    } else {
      const ors: string[] = [];
      if (scope.bookingId != null) ors.push(`booking_id.eq.${scope.bookingId}`);
      if (scope.userId) ors.push(`user_id.eq.${scope.userId}`);
      // Tenant-scope only — exclude payment-scoped notes from the profile feed.
      query = query.is("payment_id", null);
      if (ors.length) query = query.or(ors.join(","));
    }
    return query;
  });
  return hydrateNoteAuthors(
    rows.filter((r) => r.kind !== CONTRACT_INTERNAL_NOTE_KIND)
  );
}

/** {@link fetchAdminNotes} as a query. A scope pointing at nothing fetches
 *  nothing, rather than every note in the table. */
export function useAdminNotes(scope: AdminNoteScope) {
  const enabled = !!(scope.paymentId || scope.bookingId || scope.userId);
  return useQuery({
    queryKey: adminNotesKey(scope),
    enabled,
    queryFn: () => fetchAdminNotes(scope),
  });
}

/**
 * The admin-only contract note for a booking — one row, newest wins.
 *
 * Lives in `admin_notes` (kind = 'contract_internal') rather than on the booking
 * because the tenant dashboard reads its own booking with `select("*")`: a column
 * there is delivered to the tenant's browser however carefully the UI hides it.
 * admin_notes is admin-only under RLS, and a RESTRICTIVE policy limits writes of
 * this kind to super admins — so the "super admins write" rule is no longer a
 * client-side `&&`.
 */
export function useContractInternalNote(bookingId: number | null | undefined) {
  return useQuery({
    queryKey: ["v2", "admin-notes", "contract-internal", bookingId ?? null],
    enabled: !!bookingId,
    queryFn: async (): Promise<AdminNoteRow | null> => {
      if (!bookingId) return null;
      const { rows, hasKind } = await selectAdminNotes((q) =>
        q
          .eq("booking_id", bookingId)
          .order("created_at", { ascending: false })
          .limit(20)
      );
      // Without the `kind` column there is no way to tell a contract note from a
      // general one, so report "no note" rather than surfacing an unrelated one.
      if (!hasKind) return null;
      const [latest = null] = await hydrateNoteAuthors(
        rows.filter((r) => r.kind === CONTRACT_INTERNAL_NOTE_KIND)
      );
      return latest;
    },
  });
}

/**
 * Save (or clear) a booking's contract internal note.
 *
 * One live note per booking: an existing row is updated in place, an empty note
 * deletes it. RLS refuses this for anyone who is not a super admin, and the
 * error is surfaced inline by the contract card.
 */
/**
 * An UPDATE or DELETE that RLS refused comes back as ZERO ROWS AND NO ERROR — not
 * as a failure. Without this check, a caller who is not a super admin was told
 * "Internal note saved" while the row was untouched: the worst possible outcome,
 * because the admin then trusts a note that does not exist. (Verified against
 * Postgres: `DELETE 0` / `UPDATE 0`, no error, for a plain admin.) INSERT does
 * raise, so only these two verbs need it.
 */
function assertRlsLetItThrough(rows: unknown, verb: "saved" | "cleared"): void {
  if (Array.isArray(rows) && rows.length > 0) return;
  throw new Error(
    `The internal note was not ${verb}. Either the database refused the change — internal contract notes can only be written by a Super Admin — or another admin removed the note first. Reopen the tenant to see the current note.`
  );
}

export function useSaveContractInternalNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      bookingId: number;
      userId?: string | null;
      body: string;
      createdBy: string | null;
    }) => {
      const text = args.body.trim();
      const { data: existing, error: findErr } = await supabase
        .from("admin_notes")
        .select("id")
        .eq("booking_id", args.bookingId)
        .eq("kind", CONTRACT_INTERNAL_NOTE_KIND)
        .order("created_at", { ascending: false });
      if (findErr) throw findErr;
      const ids = ((existing ?? []) as { id: string }[]).map((r) => r.id);

      if (!text) {
        if (ids.length) {
          const { data, error } = await supabase
            .from("admin_notes")
            .delete()
            .in("id", ids)
            .select("id");
          if (error) throw error;
          assertRlsLetItThrough(data, "cleared");
        }
        return;
      }

      if (ids.length) {
        const { data, error } = await supabase
          .from("admin_notes")
          .update({
            body: text,
            created_by: args.createdBy ?? null,
            updated_at: new Date().toISOString(),
          })
          .eq("id", ids[0])
          .select("id");
        if (error) throw error;
        assertRlsLetItThrough(data, "saved");
        // Collapse any duplicates a previous version may have left behind.
        if (ids.length > 1) {
          await supabase.from("admin_notes").delete().in("id", ids.slice(1));
        }
        return;
      }

      // INSERT is the one verb RLS refuses with an actual error, so it needs no
      // row-count check of its own.
      const { error } = await supabase.from("admin_notes").insert({
        booking_id: args.bookingId,
        user_id: args.userId ?? null,
        body: text,
        kind: CONTRACT_INTERNAL_NOTE_KIND,
        created_by: args.createdBy ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "admin-notes"] });
    },
  });
}

/**
 * Every PAYMENT-scoped admin note on a booking, newest first.
 *
 * `useAdminNotes({ bookingId })` deliberately excludes payment-scoped rows so
 * the profile's Notes feed stays tenant-level — which also meant a note added
 * against a rent row never appeared anywhere outside that row. The Activity
 * Timeline uses this to surface them as their own events, with the month they
 * belong to resolved from the payment.
 */
export function useBookingPaymentNotes(bookingId: number | null | undefined) {
  return useQuery({
    queryKey: ["v2", "admin-notes", "by-booking-payments", bookingId ?? null],
    enabled: !!bookingId,
    queryFn: async (): Promise<AdminNoteRow[]> => {
      if (!bookingId) return [];
      // Match on the booking's PAYMENT ids rather than admin_notes.booking_id:
      // a note is guaranteed to carry the payment it was written against, but
      // booking_id depends on the caller having passed it. Same two-step shape
      // as usePaymentProofsForBooking.
      const { data: pays, error: pErr } = await supabase
        .from("payments")
        .select("id")
        .eq("booking_id", bookingId);
      if (pErr) throw pErr;
      const ids = ((pays ?? []) as { id: string }[]).map((p) => p.id);
      if (ids.length === 0) return [];
      const { rows } = await selectAdminNotes((q) =>
        q.in("payment_id", ids).order("created_at", { ascending: false })
      );
      return hydrateNoteAuthors(rows);
    },
  });
}

export type NewAdminNote = {
  body?: string | null;
  bookingId?: number | null;
  userId?: string | null;
  paymentId?: string | null;
  createdBy: string;
  /**
   * Which surface the row belongs to. Leave unset for a note somebody wrote —
   * those are the only rows the notes feed shows. Pass 'handover' for the audit
   * row a key/contract upload records, which belongs to Documents and the
   * Activity Timeline instead.
   */
  kind?: string | null;
  file?: {
    url: string;
    name: string | null;
    size: number | null;
    mimeType: string | null;
  } | null;
};

export function useAddAdminNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (note: NewAdminNote) => {
      const { error } = await supabase.from("admin_notes").insert({
        body: note.body?.trim() || null,
        booking_id: note.bookingId ?? null,
        user_id: note.userId ?? null,
        payment_id: note.paymentId ?? null,
        created_by: note.createdBy,
        // Only sent when set: an environment that predates admin_notes.kind
        // rejects the whole INSERT for an unknown column, and a plain note has
        // no reason to mention it.
        ...(note.kind ? { kind: note.kind } : {}),
        file_url: note.file?.url ?? null,
        file_name: note.file?.name ?? null,
        file_size: note.file?.size ?? null,
        mime_type: note.file?.mimeType ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "admin-notes"] });
    },
  });
}

export function useDeleteAdminNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("admin_notes")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "admin-notes"] });
    },
  });
}

type ProfilePatch = Partial<
  Pick<
    ProfileRow,
    | "email"
    | "name"
    | "full_name"
    | "phone"
    | "date_of_birth"
    | "date_of_birth_source"
    | "gender"
    | "nationality"
  >
>;

export function useUpdateProfile(userId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: ProfilePatch) => {
      if (!userId) throw new Error("No user id");
      const { error } = await supabase
        .from("profiles")
        .update(patch)
        .eq("id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "profile", userId!] });
      qc.invalidateQueries({ queryKey: ["admin-all-profiles"] });
    },
  });
}

type CreateBookingInput = {
  room_id: number;
  user_id: string;
  status: string;
  move_in_date: string;
  /** Confirmed move-out only — creation flows must pass null; the contract's
   * earliest possible move-out lives in end_date. Setting this arms the
   * automated move-out email chains. */
  move_out_date: string | null;
  start_date: string;
  end_date: string;
  monthly_rent: number | null;
  deposit_amount: number | null;
  // Denormalised tenant identity on the booking. Room/apartment views fall back
  // to these when the profile join is unavailable, so a manually created tenant
  // must carry name/email here too (COL-261).
  name?: string | null;
  email?: string | null;
};

export function useCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      input,
    }: {
      input: CreateBookingInput;
      editorId: string;
    }) => {
      const { data, error } = await supabase
        .from("bookings")
        .insert(
          operatorStamped<"bookings">({
            room_id: input.room_id,
            user_id: input.user_id,
            status: input.status,
            move_in_date: input.move_in_date,
            move_out_date: input.move_out_date,
            start_date: input.start_date,
            end_date: input.end_date,
            monthly_rent: input.monthly_rent,
            deposit_amount: input.deposit_amount,
            name: input.name ?? null,
            email: input.email ?? null,
            // Creating a tenant straight into a moved-in status IS a move-in
            // confirmation — record it, because the Active journey gate reads
            // this timestamp and not `status`. (The tenant still can't read as
            // Active until contract, deposit and keys are on file.)
            move_in_confirmed_at: [
              "active",
              "notice_given",
              "move_out_pending",
            ].includes(String(input.status ?? ""))
              ? new Date().toISOString()
              : null,
            move_out_notice_at: ["notice_given", "move_out_pending"].includes(
              String(input.status ?? "")
            )
              ? new Date().toISOString()
              : null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
        )
        .select("id")
        .single();
      if (error) throw error;
      return data as { id: number };
    },
    onSuccess: () => {
      invalidateOccupancyViews(qc);
    },
  });
}

// ─── Payments activity (reminders, emails, marked-paid events) ─────────────

export type PaymentsActivityRow = {
  id: string;
  booking_id: number;
  event_type:
    | "payment_received"
    | "reminder_sent"
    | "reminder_draft_saved"
    | "marked_paid"
    | "inkasso_referred"
    | "admin_email_sent"
    | string;
  stage: number | null;
  amount_eur: number | null;
  email_lang: string | null;
  note: string | null;
  email_to: string | null;
  email_subject: string | null;
  created_at: string;
  created_by: string | null;
};

export function usePaymentsActivity(bookingId: number | null | undefined) {
  return useQuery({
    queryKey: bookingId
      ? ["v2", "payments-activity", bookingId]
      : ["v2", "payments-activity", "noop"],
    queryFn: async (): Promise<PaymentsActivityRow[]> => {
      if (!bookingId) return [];
      const { data, error } = await supabase
        .from("payments_activity")
        .select(
          "id, booking_id, event_type, stage, amount_eur, email_lang, note, email_to, email_subject, created_at, created_by"
        )
        .eq("booking_id", bookingId)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as PaymentsActivityRow[];
    },
    enabled: !!bookingId,
  });
}

// ─── Handover protocols (move-in / move-out checklists) ────────────────────

export type HandoverProtocolRow = {
  id: string;
  ref: string;
  booking_id: number;
  user_id: string | null;
  kind: "move_in" | "move_out";
  status: "draft" | "submitted";
  signed_at: string | null;
  submitted_at: string | null;
  email_sent_at: string | null;
  created_at: string;
  pdf_storage_path: string | null;
  /** Attribution for a protocol an admin filed on the tenant's behalf.
   *  Null on every environment that predates 20260818100000. */
  recorded_by_user_id?: string | null;
  recorded_by_name?: string | null;
  recorded_at?: string | null;
  admin_note?: string | null;
  /** Set when the tick deemed this protocol submitted (20260820150000) —
   *  the window closed with no walkthrough. NULL on a real submission. */
  auto_submitted_at?: string | null;
};

const HANDOVER_BASE_COLS =
  "id, ref, booking_id, user_id, kind, status, signed_at, submitted_at, email_sent_at, created_at, pdf_storage_path";

/** Added by 20260818100000 / 20260820150000 — absent on an environment that
 *  is a push behind either migration. */
const HANDOVER_RECORDED_COLS =
  "recorded_by_user_id, recorded_by_name, recorded_at, admin_note, auto_submitted_at";

/**
 * Read `handover_protocols` at the widest column list this database supports.
 *
 * Selecting a column the database lacks fails the WHOLE query (PostgREST
 * 42703), which on a project one migration behind would blank the protocol
 * cards, the Documents tab and the deposit-bundle readiness rather than just
 * dropping the attribution. So ask for everything, and step down on a missing
 * column ALONE — a transient 500 must keep throwing, or an outage would quietly
 * be reported as "this environment has no attribution".
 *
 * Not memoised the way `selectAdminNotes` is: two queries on one screen, versus
 * a notes probe every surface re-runs.
 */
async function selectHandoverProtocols(
  /** Applies the filters/ordering to an already-`select()`ed query. */
  build: (query: any) => Promise<{ data: unknown; error: unknown }>
): Promise<HandoverProtocolRow[]> {
  const wide = await build(
    supabase
      .from("handover_protocols")
      .select(`${HANDOVER_BASE_COLS}, ${HANDOVER_RECORDED_COLS}`)
  );
  if (!wide.error) return (wide.data ?? []) as HandoverProtocolRow[];
  if (!isMissingColumn(wide.error)) throw wide.error;

  const narrow = await build(
    supabase.from("handover_protocols").select(HANDOVER_BASE_COLS)
  );
  if (narrow.error) throw narrow.error;
  return (narrow.data ?? []) as HandoverProtocolRow[];
}

export function useHandoverProtocols(bookingId: number | null | undefined) {
  return useQuery({
    queryKey: bookingId
      ? ["v2", "handover-protocols", bookingId]
      : ["v2", "handover-protocols", "noop"],
    queryFn: async (): Promise<HandoverProtocolRow[]> => {
      if (!bookingId) return [];
      return selectHandoverProtocols((q) =>
        q.eq("booking_id", bookingId).order("created_at", { ascending: false })
      );
    },
    enabled: !!bookingId,
  });
}

// ─── Payment proofs (tenant-uploaded bank transfer evidence) ───────────────

export type PaymentProofRow = {
  id: string;
  payment_id: string;
  file_name: string;
  file_path: string | null;
  file_size: number | null;
  mime_type: string | null;
  status: "pending_review" | "approved" | "rejected" | string;
  admin_comment: string | null;
  rejection_reason: string | null;
  reviewed_at: string | null;
  created_at: string;
};

export function usePaymentProofsForBooking(
  bookingId: number | null | undefined
) {
  return useQuery({
    queryKey: bookingId
      ? ["v2", "payment-proofs", bookingId]
      : ["v2", "payment-proofs", "noop"],
    queryFn: async (): Promise<PaymentProofRow[]> => {
      if (!bookingId) return [];
      const { data: payments, error: pErr } = await supabase
        .from("payments")
        .select("id")
        .eq("booking_id", bookingId);
      if (pErr) throw pErr;
      const ids = ((payments ?? []) as { id: string }[]).map((p) => p.id);
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .from("payment_proofs")
        .select(
          "id, payment_id, file_name, file_path, file_size, mime_type, status, admin_comment, rejection_reason, reviewed_at, created_at"
        )
        .in("payment_id", ids)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as PaymentProofRow[];
    },
    enabled: !!bookingId,
  });
}

// ─── Full handover protocol bundle (inventory + defects for all protocols) ──

export type ProtocolInventoryItem = {
  id: string;
  protocol_id: string;
  label: string;
  meta: string | null;
  condition: "good" | "wear" | "damaged" | null;
  defect_description: string | null;
  defect_severity: "low" | "medium" | "high" | null;
  ordering: number;
};

export type ProtocolDefect = {
  id: string;
  protocol_id: string;
  area: string;
  description: string;
  severity: "low" | "medium" | "high";
};

export type HandoverProtocolBundle = HandoverProtocolRow & {
  inventory: ProtocolInventoryItem[];
  defects: ProtocolDefect[];
  photoCount: number;
};

export function useHandoverProtocolBundles(
  bookingId: number | null | undefined
) {
  return useQuery({
    queryKey: bookingId
      ? ["v2", "handover-bundles", bookingId]
      : ["v2", "handover-bundles", "noop"],
    queryFn: async (): Promise<HandoverProtocolBundle[]> => {
      if (!bookingId) return [];
      const rows = await selectHandoverProtocols((q) =>
        q.eq("booking_id", bookingId).order("created_at", { ascending: false })
      );
      if (rows.length === 0) return [];

      const ids = rows.map((r) => r.id);

      const [invRes, defRes, photoRes] = await Promise.all([
        supabase
          .from("handover_protocol_inventory")
          .select(
            "id, protocol_id, label, meta, condition, defect_description, defect_severity, ordering"
          )
          .in("protocol_id", ids)
          .order("ordering"),
        supabase
          .from("handover_protocol_defects")
          .select("id, protocol_id, area, description, severity")
          .in("protocol_id", ids),
        supabase
          .from("handover_protocol_photos")
          .select("id, protocol_id")
          .in("protocol_id", ids),
      ]);

      const inventory = (invRes.data ?? []) as ProtocolInventoryItem[];
      const defects = (defRes.data ?? []) as ProtocolDefect[];
      const photos = (photoRes.data ?? []) as {
        id: string;
        protocol_id: string;
      }[];

      return rows.map((r) => ({
        ...r,
        inventory: inventory.filter((i) => i.protocol_id === r.id),
        defects: defects.filter((d) => d.protocol_id === r.id),
        photoCount: photos.filter((p) => p.protocol_id === r.id).length,
      }));
    },
    enabled: !!bookingId,
  });
}

// ─── KeyNest inheritance (property → apartment → room) ──────────────────────
// The schema models KeyNest pickup as inheritable: a room falls back to its
// apartment, then its property, for the shared fields (location, address,
// access link, pickup method). Codes / opening hours only exist at room level.

/** Level a resolved KeyNest value came from (null = nowhere). */
export type KeynestSource = "room" | "apartment" | "property" | null;

/** Fetch the apartment- and property-level KeyNest fields for inheritance. */
export function useKeynestInheritance(
  propertyId: number | null | undefined,
  apartmentId: number | null | undefined
) {
  return useQuery({
    queryKey: [
      "v2",
      "keynest-inherit",
      propertyId ?? null,
      apartmentId ?? null,
    ],
    enabled: !!(propertyId || apartmentId),
    queryFn: async () => {
      let apt: any = null;
      let prop: any = null;
      if (apartmentId) {
        const { data } = await supabase
          .from("apartments")
          .select(
            "keynest_pickup_location, keynest_pickup_address, pickup_method, pickup_instructions"
          )
          .eq("id", apartmentId)
          .maybeSingle();
        apt = data ?? null;
      }
      if (propertyId) {
        const { data } = await supabase
          .from("properties")
          .select(
            "keynest_pickup_location, keynest_pickup_address, keynest_access_url, pickup_method, pickup_instructions"
          )
          .eq("id", propertyId)
          .maybeSingle();
        prop = data ?? null;
      }
      return { apt, prop };
    },
  });
}

/**
 * Merge inherited KeyNest values onto a room so the tenant profile shows the
 * effective pickup details. Room value wins; else apartment; else property.
 * Returns the room object with the shared KeyNest fields resolved.
 */
export function resolveRoomKeynest(
  room: any | null | undefined,
  inherit: { apt: any; prop: any } | null | undefined
): any {
  if (!room) return room;
  const apt = inherit?.apt;
  const prop = inherit?.prop;
  const present = (v: unknown) => v != null && String(v).trim() !== "";
  // Room wins, else apartment, else property — tracking which level supplied it.
  const resolve = (
    roomVal: unknown,
    aptVal: unknown,
    propVal: unknown
  ): { value: string | null; source: KeynestSource } => {
    if (present(roomVal)) return { value: roomVal as string, source: "room" };
    if (present(aptVal))
      return { value: aptVal as string, source: "apartment" };
    if (present(propVal))
      return { value: propVal as string, source: "property" };
    return { value: null, source: null };
  };
  const shop = resolve(
    room.keynest_partner_shop_name,
    apt?.keynest_pickup_location,
    prop?.keynest_pickup_location
  );
  const addr = resolve(
    room.keynest_partner_shop_address,
    apt?.keynest_pickup_address,
    prop?.keynest_pickup_address
  );
  const url = resolve(room.keynest_access_url, null, prop?.keynest_access_url);
  const method = resolve(
    room.pickup_method,
    apt?.pickup_method,
    prop?.pickup_method
  );
  // Free-text pickup instructions shown to the tenant — inherited the same way.
  const instructions = resolve(
    room.pickup_instructions,
    apt?.pickup_instructions,
    prop?.pickup_instructions
  );
  return {
    ...room,
    keynest_partner_shop_name: shop.value,
    keynest_partner_shop_address: addr.value,
    keynest_access_url: url.value,
    pickup_method: method.value,
    pickup_instructions: instructions.value,
    __keynestSources: {
      partner_shop_name: shop.source,
      partner_shop_address: addr.source,
      access_url: url.source,
      pickup_method: method.source,
      pickup_instructions: instructions.source,
    } as Record<string, KeynestSource>,
  };
}

// ─── Issue comments (support messages in the activity feed) ────────────────

export type IssueCommentRow = {
  id: string;
  issue_id: string;
  user_id: string;
  comment: string;
  is_internal: boolean | null;
  created_at: string;
};

export function useIssueComments(issueIds: string[]) {
  return useQuery({
    queryKey: issueIds.length
      ? ["v2", "issue-comments", ...issueIds.slice().sort()]
      : ["v2", "issue-comments", "noop"],
    queryFn: async (): Promise<IssueCommentRow[]> => {
      if (issueIds.length === 0) return [];
      const { data, error } = await supabase
        .from("issue_comments")
        .select("id, issue_id, user_id, comment, is_internal, created_at")
        .in("issue_id", issueIds)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as IssueCommentRow[];
    },
    enabled: issueIds.length > 0,
  });
}
